import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { ArrowLeft, ExternalLink, MapPin, Calendar, DollarSign, User, Building, Phone, Mail, Filter, Search, Download, RefreshCw, Send, Eye, ShoppingCart, QrCode, FileDown } from 'lucide-react';
import { adminAuth } from '../services/adminAuth';
import { propertyTokenGenerator } from '../services/tokenGenerator';
import { campaignTracking } from '../services/campaignTracking';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface ScrapedProperty {
  id: string;
  campaign_id: string;
  campaign_type: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip_code: string;
  date_sold: string;
  sale_price: number;
  // Buyer's agent information
  buyers_agent_name: string;
  buyers_agent_brokerage: string;
  buyers_agent_license: string;
  buyers_agent_mobile: string;
  buyers_agent_email: string;
  // Seller's agent information (for under contract campaigns)
  sellers_agent_name?: string;
  sellers_agent_brokerage?: string;
  sellers_agent_license?: string;
  sellers_agent_mobile?: string;
  sellers_agent_email?: string;
  // URLs and images
  homes_property_url: string;
  homes_main_image_url: string;
  // Zillow-specific fields
  zillow_property_id?: string;
  zillow_url?: string;
  zillow_image_url?: string;
  // Redfin-specific fields
  redfin_main_image_url?: string;
  // Property details
  beds?: number;
  baths?: number;
  area?: number;
  latitude?: number;
  longitude?: number;
  property_status?: string;
  currency?: string;
  lot_area_string?: string;
  // Generated Artwork URLs (dedicated columns)
  watercolor_image_url?: string;
  oil_image_url?: string;
  pencil_image_url?: string;
  charcoal_image_url?: string;
  // Access code for unique property links
  access_code?: string;
  qr_code_url?: string; // QR code image URL
  source_platform: string;
  processing_status: string;
  created_at: string;
  raw_data: any;
  suitability_score?: number;
  suitability_analysis?: string;
  // Legacy address fields for backward compatibility
  address?: string;
  street_address?: string;
  token?: string;
  configurator_opened_count?: number;
  checkout_initiated_count?: number;
  purchase_amount?: number;
  purchase_completed_at?: string;
  last_activity_at?: string;
  outreach_status?: string;
  outreach_sent_at?: string;
  payment_page_accessed_count?: number;
  configurator_opened_at?: string;
  landing_page_accessed_count?: number;
  landing_page_accessed_at?: string;
  order_summary_accessed_count?: number;
  order_summary_accessed_at?: string;
  checkout_initiated_at?: string;
  payment_page_accessed_at?: string;
}

interface Campaign {
  id: string;
  name: string;
  campaign_type: string;
  campaign_subtype: string;
  total_properties_found: number;
  created_at: string;
  screenshot_thumbnail_url?: string;
  source_platform?: string;
}

const CampaignPropertiesTable: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign_id');
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [properties, setProperties] = useState<ScrapedProperty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // const [persistedFilteredIds, setPersistedFilteredIds] = useState<string[]>([]); // Persist filtered IDs - REMOVED for pagination
  const [sortBy, setSortBy] = useState<'date_sold' | 'sale_price' | 'address' | 'suitability' | 'import_order'>('import_order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [activeTab, setActiveTab] = useState<'all' | 'with_emails' | 'ready_to_send'>('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(20);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, activeTab, sortBy, sortOrder]);
  
  // Dummy function to replace removed setPersistedFilteredIds
  const setPersistedFilteredIds = () => {/* No longer needed with pagination */};
  
  // Multi-select state
  const [selectedProperties, setSelectedProperties] = useState<Set<string>>(new Set());
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });
  const [fetchingAgentDetails, setFetchingAgentDetails] = useState(false);
  const [agentDetailsProgress, setAgentDetailsProgress] = useState({ current: 0, total: 0 });
  const [generatingArtwork, setGeneratingArtwork] = useState(false);
  const [artworkProgress, setArtworkProgress] = useState({ current: 0, total: 0 });
  const [generatingWatercolors, setGeneratingWatercolors] = useState(false);
  const [watercolorProgress, setWatercolorProgress] = useState({ current: 0, total: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  // Anniversary email state
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailProgress, setEmailProgress] = useState({ current: 0, total: 0 });
  const [emailError, setEmailError] = useState<string | null>(null);

  // Test email state
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailSuccess, setTestEmailSuccess] = useState<string | null>(null);

  // QR code generation state
  const [generatingQRCodes, setGeneratingQRCodes] = useState(false);
  const [qrCodeProgress, setQRCodeProgress] = useState({ current: 0, total: 0 });
  const [qrCodeMessage, setQRCodeMessage] = useState<string | null>(null);


  // New state for generating images
  const [generatingImages, setGeneratingImages] = useState(false);
  const [imageGenerationProgress, setImageGenerationProgress] = useState({ 
    currentProperty: 0, 
    totalProperties: 0, 
    currentStyle: '', 
    propertyAddress: '',
    totalImages: 0,
    successfulImages: 0
  });
  const [imageGenerationMessage, setImageGenerationMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset functionality state
  const [showResetEmailConfirm, setShowResetEmailConfirm] = useState(false);
  const [showResetOpenedConfirm, setShowResetOpenedConfirm] = useState(false);
  const [resettingEmails, setResettingEmails] = useState(false);
  const [resettingOpened, setResettingOpened] = useState(false);

  useEffect(() => {
    if (!adminAuth.isAuthenticated()) {
      navigate('/admin-login');
      return;
    }

    if (!campaignId) {
      setError('No campaign ID provided');
      setLoading(false);
      return;
    }

    loadCampaignData();
  }, [campaignId, navigate]);

  // Clear persisted filter IDs when base filters change
  useEffect(() => {
    // setPersistedFilteredIds([]); // Removed for pagination
  }, [searchTerm, statusFilter, activeTab, sortBy, sortOrder]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load campaign details
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        throw new Error(`Failed to load campaign: ${campaignError.message}`);
      }

      setCampaign(campaignData);

      // Load properties for this campaign
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('scraped_properties')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (propertiesError) {
        throw new Error(`Failed to load properties: ${propertiesError.message}`);
      }

      setProperties(propertiesData || []);
    } catch (err) {
      console.error('Error loading campaign data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  const refreshPropertiesData = async () => {
    try {
      setRefreshing(true);
      const refreshTime = new Date().toISOString();
      console.log('=== Starting Properties Refresh ===');
      console.log('Refresh time:', refreshTime);
      console.log('Campaign ID:', campaignId);
      
      if (!campaignId) {
        throw new Error('No campaign ID available for refresh');
      }
      
      // Load properties for this campaign with cache-busting timestamp
      const cacheBreaker = Date.now();
      console.log('Querying properties with cache breaker:', cacheBreaker);
      
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('scraped_properties')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (propertiesError) {
        console.error('Supabase properties query error:', propertiesError);
        console.error('Error details:', JSON.stringify(propertiesError, null, 2));
        throw new Error(`Failed to load properties: ${propertiesError.message}`);
      }

      console.log('Raw query result:', propertiesData?.length || 0, 'properties found');
      console.log('First property sample:', propertiesData?.[0] ? {
        id: propertiesData[0].id,
        address: propertiesData[0].address_line_1,
        outreach_status: propertiesData[0].outreach_status,
        configurator_opened_count: propertiesData[0].configurator_opened_count,
        configurator_opened_at: propertiesData[0].configurator_opened_at,
        last_activity_at: propertiesData[0].last_activity_at
      } : 'No properties');
      
      // Count properties with opened status
      const openedCount = propertiesData?.filter(p => 
        p.configurator_opened_count && p.configurator_opened_count > 0
      ).length || 0;
      
      console.log(`Found ${openedCount} properties with configurator_opened_count > 0`);
      
      if (openedCount > 0) {
        const openedProperties = propertiesData?.filter(p => 
          p.configurator_opened_count && p.configurator_opened_count > 0
        );
        console.log('Opened properties details:', openedProperties?.map(p => ({
          id: p.id,
          address: p.address_line_1,
          count: p.configurator_opened_count,
          status: p.outreach_status
        })));
      }
      
      // Additional debugging: Check ALL properties for any tracking fields
      console.log('🔍 Debugging all properties for tracking fields:');
      propertiesData?.forEach((p, index) => {
        if (index < 3) { // Only show first 3 for brevity
          console.log(`Property ${index + 1}:`, {
            id: p.id.slice(-8),
            address: p.address_line_1,
            outreach_status: p.outreach_status,
            configurator_opened_count: p.configurator_opened_count,
            configurator_opened_at: p.configurator_opened_at,
            checkout_initiated_count: p.checkout_initiated_count,
            payment_page_accessed_count: p.payment_page_accessed_count,
            last_activity_at: p.last_activity_at,
            access_code: p.access_code
          });
        }
      });
      
      // Force state update with new data
      console.log('Updating state with fresh data...');
      setProperties(propertiesData || []);
      
      // Clear any previous error messages
      setError(null);
      
      // Show brief refresh success message
      setRefreshMessage(`✅ Data refreshed with latest updates`);
      setTimeout(() => setRefreshMessage(null), 2000);
      
      console.log('=== Properties Refresh Completed Successfully ===');
      
    } catch (err) {
      console.error('=== Properties Refresh Failed ===');
      console.error('Error details:', err);
      console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      
      // Re-throw the error so it can be caught by the calling function
      throw err;
    } finally {
      setRefreshing(false);
    }
  };

  const handleSelectProperty = (propertyId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedProperties);
    if (isSelected) {
      newSelected.add(propertyId);
    } else {
      newSelected.delete(propertyId);
    }
    setSelectedProperties(newSelected);
  };

  const handleSelectAll = (isSelected: boolean, currentFilteredProperties: ScrapedProperty[]) => {
    if (isSelected) {
      const allIds = currentFilteredProperties.map(p => p.id);
      setSelectedProperties(new Set(allIds));
    } else {
      setSelectedProperties(new Set());
    }
  };

  const fetchDetailsForSelected = async () => {
    if (selectedProperties.size === 0) return;

    setFetchingDetails(true);
    setFetchProgress({ current: 0, total: selectedProperties.size });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      const selectedPropertiesArray = Array.from(selectedProperties);
      let processed = 0;

      for (const propertyId of selectedPropertiesArray) {
        const property = properties.find(p => p.id === propertyId);
        if (!property || !property.homes_property_url) {
          processed++;
          setFetchProgress({ current: processed, total: selectedProperties.size });
          continue;
        }

        try {
          console.log(`Fetching details for property: ${property.address_line_1}`);
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scrape-property-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              url: property.homes_property_url,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }

          const detailData = await response.json();
          
          console.log('Raw response data:', detailData);
          console.log('detailData.success:', detailData.success);
          console.log('detailData.data:', detailData.data);
          
          if (detailData.success && detailData.data) {
            // Extract property details from the response - matching the actual API response structure
            const propertyDetails = detailData.data;
            console.log('Extracted property details:', propertyDetails);
            console.log('Available fields in property details:', Object.keys(propertyDetails));
            
            // Map the response fields to database columns correctly
            const updateData = {
              // Map lastBuyersAgent object fields
              buyers_agent_name: propertyDetails.lastBuyersAgent?.name || property.buyers_agent_name,
              buyers_agent_brokerage: propertyDetails.lastBuyersAgent?.broker || property.buyers_agent_brokerage,
              buyers_agent_license: propertyDetails.lastBuyersAgent?.license || property.buyers_agent_license,
              buyers_agent_mobile: propertyDetails.lastBuyersAgent?.phone || property.buyers_agent_mobile,
              buyers_agent_email: propertyDetails.lastBuyersAgent?.email || property.buyers_agent_email,
              
              // Map address fields with more detailed data
              address_line_1: propertyDetails.fullStreetAddress || propertyDetails.streetNumber && propertyDetails.streetName 
                ? `${propertyDetails.streetNumber} ${propertyDetails.streetName}`.trim() 
                : property.address_line_1,
              city: propertyDetails.city || property.city,
              state: propertyDetails.state || property.state,
              zip_code: propertyDetails.zipCode || property.zip_code,
              
              // Map other fields
              homes_main_image_url: propertyDetails.primaryImageUrl || property.homes_main_image_url,
              date_sold: propertyDetails.dateSold || property.date_sold,
              
              // Store the full response in raw_data
              raw_data: { ...property.raw_data, details: detailData.data },
              processing_status: 'completed',
            };
            
            console.log('Update data being sent to database:', updateData);
            
            const { error: updateError } = await supabase
              .from('scraped_properties')
              .update(updateData)
              .eq('id', propertyId);

            if (updateError) {
              console.error('Error updating property:', updateError);
            } else {
              console.log('Database update successful for property:', propertyId);
              
              // Immediately update the local state to show changes
              setProperties(prevProperties => {
                const updatedProperties = prevProperties.map(p => 
                  p.id === propertyId 
                    ? {
                        ...p,
                        ...updateData
                      }
                    : p
                );
                console.log('Local state updated, new properties:', updatedProperties.find(p => p.id === propertyId));
                return updatedProperties;
              });
            }
          }
        } catch (error) {
          console.error(`Error fetching details for property ${propertyId}:`, error);
          
          // Mark as failed
          await supabase
            .from('scraped_properties')
            .update({
              processing_status: 'failed',
              raw_data: { ...property.raw_data, error: error instanceof Error ? error.message : 'Unknown error' }
            })
            .eq('id', propertyId);

          // Immediately update the local state to show failed status
          setProperties(prevProperties => 
            prevProperties.map(p => 
              p.id === propertyId 
                ? {
                    ...p,
                    processing_status: 'failed',
                    raw_data: { ...p.raw_data, error: error instanceof Error ? error.message : 'Unknown error' }
                  }
                : p
            )
          );
        }

        processed++;
        setFetchProgress({ current: processed, total: selectedProperties.size });
      }

      // Reload the data to show updates
      console.log('Bulk fetch completed, refreshing data...');
      await refreshPropertiesData();
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error fetching details:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch property details');
    } finally {
      setFetchingDetails(false);
      setFetchProgress({ current: 0, total: 0 });
    }
  };

  const fetchAgentDetailsForSelected = async () => {
    if (selectedProperties.size === 0) return;

    const isUnderContractCampaign = campaign?.campaign_subtype === 'under_contract_sellers_agent';

    // Filter to only properties that have agent information
    const propertiesWithAgents = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => {
        if (!p) return false;
        
        // For under contract campaigns, check seller agent info
        if (isUnderContractCampaign) {
          return p.sellers_agent_name && p.sellers_agent_name.trim() !== '';
        }
        // For other campaigns, check buyer agent info
        return p.buyers_agent_name && p.buyers_agent_name.trim() !== '';
      });

    if (propertiesWithAgents.length === 0) {
      setError(`No properties with ${isUnderContractCampaign ? 'seller' : 'buyer'} agent information selected`);
      return;
    }

    setFetchingAgentDetails(true);
    setAgentDetailsProgress({ current: 0, total: propertiesWithAgents.length });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      let processed = 0;

      for (const property of propertiesWithAgents) {
        try {
          const agentName = isUnderContractCampaign ? property.sellers_agent_name : property.buyers_agent_name;
          const agentLicense = isUnderContractCampaign ? property.sellers_agent_license : property.buyers_agent_license;
          const agentBrokerage = isUnderContractCampaign ? property.sellers_agent_brokerage : property.buyers_agent_brokerage;
          
          console.log(`Fetching ${isUnderContractCampaign ? 'seller' : 'buyer'} agent contact details for: ${agentName}`);
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/find-agent-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              agentName: agentName,
              agentLicense: agentLicense,
              brokerage: agentBrokerage,
              city: property.city,
              state: property.state,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }

          const agentData = await response.json();
          
          console.log('Agent details response:', agentData);
          
          if (agentData.success && agentData.data) {
            // Extract agent contact details from the response
            const agentDetails = agentData.data;
            console.log('Extracted agent details:', agentDetails);
            
            // Map the response fields to database columns based on campaign type
            const updateData: any = {
              // Store the full response in raw_data
              raw_data: { 
                ...property.raw_data, 
                agentContactDetails: agentDetails 
              },
            };
            
            // Update the appropriate agent fields based on campaign type
            if (isUnderContractCampaign) {
              // Update seller agent fields
              updateData.sellers_agent_email = agentDetails.aiFoundEmail || property.sellers_agent_email;
              updateData.sellers_agent_mobile = agentDetails.aiFoundPhone || agentDetails.aiFoundMobile || property.sellers_agent_mobile;
            } else {
              // Update buyer agent fields
              updateData.buyers_agent_email = agentDetails.aiFoundEmail || property.buyers_agent_email;
              updateData.buyers_agent_mobile = agentDetails.aiFoundPhone || agentDetails.aiFoundMobile || property.buyers_agent_mobile;
            }
            
            console.log('Agent update data being sent to database:', updateData);
            
            const { error: updateError } = await supabase
              .from('scraped_properties')
              .update(updateData)
              .eq('id', property.id);

            if (updateError) {
              console.error('Error updating property with agent details:', updateError);
              throw new Error(`Failed to update property: ${updateError.message}`);
            }

            console.log(`Agent details updated successfully for property ${property.id}`);
          } else {
            console.log(`No agent details found for property ${property.id}`);
            }

        } catch (error) {
          console.error(`Error fetching agent details for property ${property.id}:`, error);
          // Don't throw here to allow other properties to be processed
        }

        processed++;
        setAgentDetailsProgress({ current: processed, total: propertiesWithAgents.length });
      }

      // Reload the data to show updates
      console.log('Agent details fetch completed, refreshing data...');
      await refreshPropertiesData();
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error fetching agent details:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch agent details');
    } finally {
      setFetchingAgentDetails(false);
      setAgentDetailsProgress({ current: 0, total: 0 });
    }
  };

  // NEW FUNCTION: Fetch detailed Zillow property information for under contract campaigns
  const fetchZillowDetailsForSelected = async () => {
    if (selectedProperties.size === 0) return;

    // Only for under contract campaigns
    if (campaign?.campaign_subtype !== 'under_contract_sellers_agent') {
      setError('Zillow details fetching is only available for under contract campaigns');
      return;
    }

    // Filter to only properties that have Zillow URLs
    const propertiesWithZillowUrls = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => {
        if (!p) return false;
        // Check for Zillow URL in raw_data
        const zillowUrl = p.raw_data?.url || p.zillow_url;
        return zillowUrl && zillowUrl.includes('zillow.com');
      });

    if (propertiesWithZillowUrls.length === 0) {
      setError('No properties with Zillow URLs selected');
      return;
    }

    setFetchingDetails(true);
    setFetchProgress({ current: 0, total: propertiesWithZillowUrls.length });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Clear any previous messages
      setSuccessMessage(null);
      setError(null);

      let processed = 0;
      let skippedCount = 0;
      let successCount = 0;

      for (const property of propertiesWithZillowUrls) {
        try {
          const zillowUrl = property.raw_data?.url || property.zillow_url;
          console.log(`Fetching Zillow details for property ${property.id}: ${zillowUrl}`);
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-zillow-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              url: zillowUrl,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const detailData = await response.json()
          console.log('Received Zillow detail data:', detailData)

          // Handle both success and graceful failure cases
          if (detailData.success === false && detailData.error === 'property_details_unavailable') {
            console.warn(`Property details unavailable for property ${property.id}:`, detailData.message)
            console.warn('Available response keys:', detailData.data?.availableKeys)
            
            // Skip this property but don't fail the entire batch
            skippedCount++;
            setFetchProgress(prev => ({ ...prev, current: prev.current + 1 }))
            continue
          }

          if (detailData.success && detailData.data) {
            const propertyDetails = detailData.data.property
            const agentLookup = detailData.data.agentContactLookup
            console.log('Extracted Zillow property details:', propertyDetails)
            console.log('Agent contact lookup results:', agentLookup)
            
            // Map the response fields to database columns - only update fields that exist in our schema
            const updateData: any = {
              // Update address if available (but preserve existing if new data is empty)
              address_line_1: propertyDetails.address_line_1 || property.address_line_1,
              city: propertyDetails.city || property.city,
              state: propertyDetails.state || property.state,
              zip_code: propertyDetails.zip_code || property.zip_code,
              
              // Update price and status
              sale_price: propertyDetails.sale_price || property.sale_price,
              property_status: propertyDetails.property_status || property.property_status,
              
              // Update seller agent information (now includes AI-found contact details!)
              sellers_agent_name: propertyDetails.sellers_agent_name || property.sellers_agent_name,
              sellers_agent_brokerage: propertyDetails.sellers_agent_brokerage || property.sellers_agent_brokerage,
              sellers_agent_mobile: propertyDetails.sellers_agent_mobile || property.sellers_agent_mobile,
              sellers_agent_email: propertyDetails.sellers_agent_email || property.sellers_agent_email,
              
              // Store the complete enhanced data including agent contact lookup results
              raw_data: { 
                ...property.raw_data, 
                zillowDetails: propertyDetails.raw_data,
                agentContactLookup: agentLookup
              },
              
              processing_status: 'completed',
            }
            
            console.log('Enhanced update data with agent contact details:', updateData)
            
            const { error: updateError } = await supabase
              .from('scraped_properties')
              .update(updateData)
              .eq('id', property.id)

            if (updateError) {
              console.error('Error updating property with enhanced Zillow details:', updateError)
              throw new Error(`Failed to update property: ${updateError.message}`)
            }

            console.log(`Enhanced Zillow details with agent contacts updated successfully for property ${property.id}`)
            successCount++;
          } else {
            console.log(`No enhanced Zillow details found for property ${property.id}`)
          }

        } catch (error) {
          console.error(`Error fetching Zillow details for property ${property.id}:`, error);
          // Don't throw here to allow other properties to be processed
        }

        processed++;
        setFetchProgress({ current: processed, total: propertiesWithZillowUrls.length });
      }

      // Show completion summary (data is saved, manual refresh will show updates)
      console.log('Zillow details fetch completed successfully');
      
      // Auto-refresh the data with improved error handling
      try {
        // Give extra time for database changes to propagate  
        console.log('Waiting for database changes to propagate...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        console.log('Refreshing data to show updates...');
        await refreshPropertiesData();
        console.log('Auto-refresh completed successfully');
        
        // Show success message
        if (skippedCount > 0) {
          setSuccessMessage(`✅ Completed: ${successCount} properties updated, ${skippedCount} properties skipped (no details available)`);
        } else {
          setSuccessMessage(`✅ Successfully updated ${successCount} properties with Zillow details and agent contacts`);
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } catch (refreshError) {
        console.error('Auto-refresh failed (non-critical):', refreshError);
        
        // Show success but mention that manual refresh may be needed
        if (skippedCount > 0) {
          setSuccessMessage(`✅ Completed: ${successCount} properties updated, ${skippedCount} properties skipped. Updates saved - refresh page if not visible.`);
        } else {
          setSuccessMessage(`✅ Successfully updated ${successCount} properties with Zillow details and agent contacts. Updates saved - refresh page if not visible.`);
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      }
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error fetching Zillow details:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch Zillow details');
    } finally {
      setFetchingDetails(false);
      setFetchProgress({ current: 0, total: 0 });
    }
  };

  const generateAllStylesForZillowProperties = async () => {
    console.log('=== generateAllStylesForZillowProperties CALLED ===');
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Get all selected properties
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p);

    if (selectedPropertiesArray.length === 0) {
      console.log('No valid properties found, setting error');
      setError('No valid properties selected');
      return;
    }

    console.log('Starting unified artwork generation for all 3 styles...');
    setGeneratingArtwork(true);
    setArtworkProgress({ current: 0, total: selectedPropertiesArray.length });
    
    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Clear any previous messages
      setSuccessMessage(null);
      setError(null);

      // Helper function to create address string
      const createAddressString = (property: any) => {
        const hasAddress = property.address_line_1 && typeof property.address_line_1 === 'string' && property.address_line_1.trim() !== '';
        const hasCity = property.city && typeof property.city === 'string' && property.city.trim() !== '';
        const hasState = property.state && typeof property.state === 'string' && property.state.trim() !== '';
        const hasZip = property.zip_code && typeof property.zip_code === 'string' && property.zip_code.trim() !== '';
        
        if (hasAddress || hasCity || hasState) {
          const addressParts = [];
          
          if (hasAddress) {
            addressParts.push(property.address_line_1.trim());
          }
          
          const locationParts = [];
          if (hasCity) locationParts.push(property.city.trim());
          if (hasState) locationParts.push(property.state.trim());
          if (hasZip) locationParts.push(property.zip_code.trim());
          
          if (locationParts.length > 0) {
            addressParts.push(locationParts.join(', '));
          }
          
          return addressParts.join(', ');
        } else {
          return `Property ${property.id}`;
        }
      };

      // Process in batches of 3
      console.log(`🎨 Processing ${selectedPropertiesArray.length} Zillow properties in batches of 3 for all styles...`);
      
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < selectedPropertiesArray.length; i += batchSize) {
        batches.push(selectedPropertiesArray.slice(i, i + batchSize));
      }

      let processed = 0;

      for (const [batchIndex, batch] of batches.entries()) {
        console.log(`🎨 Processing unified batch ${batchIndex + 1}/${batches.length} (${batch.length} properties)`);
        
        try {
          // Prepare batch data for all styles
          const batchData = batch.map(property => ({
            propertyId: property.id,
            zillowImageUrl: property.zillow_image_url || null,
            address: createAddressString(property)
          }));

          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-zillow-artwork-batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              properties: batchData,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const batchResult = await response.json();
          console.log(`✅ Unified batch ${batchIndex + 1} completed:`, batchResult);

          // Log success results
          if (batchResult.successful && batchResult.successful.length > 0) {
            console.log(`✅ ${batchResult.successful.length} properties succeeded in batch ${batchIndex + 1}:`);
            batchResult.successful.forEach((success: any) => {
              console.log(`  - ${success.address} (${success.propertyId}): Generated ${success.successfulStyles}/${success.totalStyles} styles`);
            });
          }

          // Log failed results
          if (batchResult.failed && batchResult.failed.length > 0) {
            console.warn(`❌ ${batchResult.failed.length} properties failed in batch ${batchIndex + 1}:`);
            batchResult.failed.forEach((failure: any) => {
              console.warn(`  - ${failure.address} (${failure.propertyId}): ${failure.error}`);
            });
          }

          // Update progress
          processed += batch.length;
          setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });

        } catch (error) {
          console.error(`❌ Error processing unified batch ${batchIndex + 1}:`, error);
          // Continue with next batch even if this one fails
          processed += batch.length;
          setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });
        }
      }

      // Reload the data to show updates
      console.log('Unified artwork generation completed, refreshing data...');
      await refreshPropertiesData();
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error generating unified artwork:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate artwork');
    } finally {
      setGeneratingArtwork(false);
      setArtworkProgress({ current: 0, total: 0 });
    }
  };

  const generateArtworkForSelected = async (artworkStyle: string = 'watercolor') => {
    console.log('=== generateArtworkForSelected CALLED ===');
    console.log('selectedProperties.size:', selectedProperties.size);
    console.log('selectedProperties:', Array.from(selectedProperties));
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Get all selected properties (not just those with addresses)
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p);

    console.log('selectedPropertiesArray:', selectedPropertiesArray.length);
    console.log('First property:', selectedPropertiesArray[0]);

    if (selectedPropertiesArray.length === 0) {
      console.log('No valid properties found, setting error');
      setError('No valid properties selected');
      return;
    }

    // Check if these are Zillow properties - if so, use the unified batch processing
    const sourcePlatform = campaign?.source_platform;
    if (sourcePlatform === 'zillow' || campaign?.campaign_subtype === 'under_contract_sellers_agent') {
      console.log('Zillow properties detected, using unified batch processing for all 3 styles...');
      return await generateAllStylesForZillowProperties();
    }

    console.log('Starting artwork generation...');
    setGeneratingArtwork(true);
    setArtworkProgress({ current: 0, total: selectedPropertiesArray.length });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      let processed = 0;

      // Helper function to create address string
      const createAddressString = (property: any) => {
          const hasAddress = property.address_line_1 && typeof property.address_line_1 === 'string' && property.address_line_1.trim() !== '';
          const hasCity = property.city && typeof property.city === 'string' && property.city.trim() !== '';
          const hasState = property.state && typeof property.state === 'string' && property.state.trim() !== '';
          const hasZip = property.zip_code && typeof property.zip_code === 'string' && property.zip_code.trim() !== '';
          
          if (hasAddress || hasCity || hasState) {
            const addressParts = [];
            
            if (hasAddress) {
              addressParts.push(property.address_line_1.trim());
            }
            
            const locationParts = [];
            if (hasCity) {
              locationParts.push(property.city.trim());
            }
            if (hasState) {
              locationParts.push(property.state.trim());
            }
            if (hasZip) {
              locationParts.push(property.zip_code.trim());
            }
            
            if (locationParts.length > 0) {
              addressParts.push(locationParts.join(', '));
            }
            
          return addressParts.join(', ');
          } else {
          return `Property ${property.id}`;
        }
      };

      // Separate properties by platform for different processing strategies
      const redfinProperties = [];
      const otherProperties = [];
      
      for (const property of selectedPropertiesArray) {
        const sourcePlatform = campaign?.source_platform;
        
        if (sourcePlatform === 'redfin' && property.redfin_main_image_url) {
          redfinProperties.push({
            property,
            redfinImageUrl: property.redfin_main_image_url,
            address: createAddressString(property),
            propertyId: property.id
          });
        } else {
          otherProperties.push(property);
        }
      }

      // Separate Zillow properties for batch processing
      const zillowProperties = otherProperties.filter(property => 
        campaign?.source_platform === 'zillow' || 
        campaign?.campaign_subtype === 'under_contract_sellers_agent'
      );
      const remainingOtherProperties = otherProperties.filter(property => 
        !(campaign?.source_platform === 'zillow' || 
          campaign?.campaign_subtype === 'under_contract_sellers_agent')
      );

      console.log(`🚀 Processing artwork generation - Redfin batch: ${redfinProperties.length}, Zillow batch: ${zillowProperties.length}, Others: ${remainingOtherProperties.length}`);

      // Process Redfin properties in batches of 3 (concurrent)
      if (redfinProperties.length > 0) {
        console.log(`🎨 Processing ${redfinProperties.length} Redfin properties in batches of 3...`);
        
        // Split into batches of 3
        const batchSize = 3;
        const batches = [];
        for (let i = 0; i < redfinProperties.length; i += batchSize) {
          batches.push(redfinProperties.slice(i, i + batchSize));
        }

        for (const [batchIndex, batch] of batches.entries()) {
          console.log(`🎨 Processing Redfin batch ${batchIndex + 1}/${batches.length} (${batch.length} properties)`);
          
          try {
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-redfin-artwork`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'X-Admin-Token': adminToken,
              },
              body: JSON.stringify({
                properties: batch,
                style: 'watercolor',
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const batchResult = await response.json();
            console.log(`✅ Batch ${batchIndex + 1} completed:`, batchResult.summary);

            // Process successful results
            if (batchResult.successful && batchResult.successful.length > 0) {
              for (const artworkResult of batchResult.successful) {
                try {
                  console.log('Artwork generation successful for property:', artworkResult.propertyId);
                  console.log('Artwork result data:', artworkResult);
                  
                  // Generate unique access code for this property
                  const { data: accessCodeResult, error: accessCodeError } = await supabase
                    .rpc('generate_property_access_code');
                  
                  if (accessCodeError) {
                    console.error('Error generating access code:', accessCodeError);
                  }
                  
                  const generatedAccessCode = accessCodeResult || null;
                  console.log('Generated access code:', generatedAccessCode);

                  // Update the property data with access code, artwork URL, and suitability score
                  const updateData = { 
                    access_code: generatedAccessCode,
                    watercolor_image_url: artworkResult.artworkUrl
                  };
                  
                  // Include suitability score if available (backend already stored it, but this ensures consistency)
                  if (artworkResult.suitabilityScore !== undefined) {
                    updateData.suitability_score = artworkResult.suitabilityScore;
                  }
                  if (artworkResult.analysisResult) {
                    updateData.suitability_analysis = artworkResult.analysisResult;
                  }
                  
                  console.log('Update data:', updateData);

                  const { error: updateError } = await supabase
                    .from('scraped_properties')
                    .update(updateData)
                    .eq('id', artworkResult.propertyId);

                  if (updateError) {
                    console.error('Error updating property with access code and suitability score:', updateError);
                  } else {
                    console.log('✅ Property updated with access code, artwork URL, and suitability score');
                    
                    // Update local state immediately to show changes
                    setProperties(prevProperties => {
                      return prevProperties.map(p => 
                        p.id === artworkResult.propertyId 
                          ? {
                              ...p,
                              ...updateData
                            }
                          : p
                      );
                    });
                  }
                } catch (error) {
                  console.error('Error processing successful artwork result:', error);
                }
              }
            }

            // Log failed results
            if (batchResult.failed && batchResult.failed.length > 0) {
              console.warn(`❌ ${batchResult.failed.length} properties failed in batch ${batchIndex + 1}:`);
              batchResult.failed.forEach((failure: any) => {
                console.warn(`  - ${failure.address} (${failure.propertyId}): ${failure.error}`);
              });
            }

            // Update progress
            processed += batch.length;
            setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });

          } catch (error) {
            console.error(`❌ Error processing Redfin batch ${batchIndex + 1}:`, error);
            // Continue with next batch even if this one fails
            processed += batch.length;
            setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });
          }
        }
      }

      // Process Zillow properties in batches of 3 (concurrent)
      if (zillowProperties.length > 0) {
        console.log(`🎨 Processing ${zillowProperties.length} Zillow properties in batches of 3...`);
        
        // Split into batches of 3
        const batchSize = 3;
        const batches = [];
        for (let i = 0; i < zillowProperties.length; i += batchSize) {
          batches.push(zillowProperties.slice(i, i + batchSize));
        }

        for (const [batchIndex, batch] of batches.entries()) {
          console.log(`🎨 Processing Zillow batch ${batchIndex + 1}/${batches.length} (${batch.length} properties)`);
          
          try {
            // Prepare batch data for Zillow properties
            const batchData = batch.map(property => ({
              propertyId: property.id,
              zillowImageUrl: property.zillow_image_url || null,
              address: createAddressString(property),
              style: artworkStyle
            }));

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-zillow-artwork-batch`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'X-Admin-Token': adminToken,
              },
              body: JSON.stringify({
                properties: batchData,
              }),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const batchResult = await response.json();
            console.log(`✅ Zillow batch ${batchIndex + 1} completed:`, batchResult);

            // Log success results
            if (batchResult.successful && batchResult.successful.length > 0) {
              console.log(`✅ ${batchResult.successful.length} properties succeeded in batch ${batchIndex + 1}:`);
              batchResult.successful.forEach((success: any) => {
                console.log(`  - ${success.address} (${success.propertyId}): Generated artwork`);
              });
            }

            // Log failed results
            if (batchResult.failed && batchResult.failed.length > 0) {
              console.warn(`❌ ${batchResult.failed.length} properties failed in batch ${batchIndex + 1}:`);
              batchResult.failed.forEach((failure: any) => {
                console.warn(`  - ${failure.address} (${failure.propertyId}): ${failure.error}`);
              });
            }

            // Update progress
            processed += batch.length;
            setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });

          } catch (error) {
            console.error(`❌ Error processing Zillow batch ${batchIndex + 1}:`, error);
            // Continue with next batch even if this one fails
            processed += batch.length;
            setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });
          }
        }
      }

      // Process remaining other properties individually (existing logic)
      for (const property of remainingOtherProperties) {
        try {
          const addressForArtwork = createAddressString(property);
          
          console.log(`Generating artwork for: "${addressForArtwork}" (Property ID: ${property.id})`);
          console.log('Campaign source platform:', campaign?.source_platform);
          console.log('Available image URLs:', {
            redfin: property.redfin_main_image_url,
            zillow: property.zillow_image_url,
            homes: property.homes_main_image_url
          });

          // Use generic artwork generation (Make.com scraping) for remaining properties
          console.log('Using generic artwork generation (Make.com scraping)');
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-artwork-webhook`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'X-Admin-Token': adminToken,
              },
              body: JSON.stringify({
                address: addressForArtwork,
                style: artworkStyle,
                request_id: `property_${property.id}_${Date.now()}`,
              }),
            });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }

          const artworkData = await response.json();
          
          console.log('Artwork generation response:', artworkData);
          
          if (artworkData.success && artworkData.data) {
            // Extract artwork details from the response
            const artworkDetails = artworkData.data;
            console.log('Extracted artwork details:', artworkDetails);
            
            // Generate unique access code for this property
            console.log('Generating unique access code for property:', property.id);
            const { data: accessCodeResult, error: accessCodeError } = await supabase
              .rpc('generate_property_access_code');
            
            if (accessCodeError) {
              console.error('Error generating access code:', accessCodeError);
              // Continue without access code - don't fail artwork generation
            }
            
            const generatedAccessCode = accessCodeResult || null;
            console.log('Generated access code:', generatedAccessCode);
            
            // Map the response fields to database columns
            const updateData = {
              // Store metadata in raw_data (but not the artwork URL since it's in dedicated columns)
              raw_data: { 
                ...property.raw_data, 
                artworkGeneration: {
                  // Remove artworkUrl from JSON storage - it's now in dedicated columns
                  style: artworkDetails.style,
                  request_id: artworkDetails.request_id,
                  address_used: addressForArtwork,
                  generated_at: new Date().toISOString(),
                  suitabilityScore: artworkDetails.suitabilityScore,
                  analysisResult: artworkDetails.analysisResult,
                  sourceImageUrl: artworkDetails.zillowImageUrl || artworkDetails.redfinImageUrl,
                  access_code_generated: !!generatedAccessCode
                }
              },
              // Store access code in dedicated column
              access_code: generatedAccessCode,
            };

            // Also store in the dedicated column based on style
            const style = artworkDetails.style?.toLowerCase();
            if (style === 'watercolor') {
              updateData.watercolor_image_url = artworkDetails.artworkUrl;
            } else if (style === 'oil') {
              updateData.oil_image_url = artworkDetails.artworkUrl;
            } else if (style === 'pencil') {
              updateData.pencil_image_url = artworkDetails.artworkUrl;
            } else if (style === 'charcoal') {
              updateData.charcoal_image_url = artworkDetails.artworkUrl;
            }
            
            console.log('Artwork update data being sent to database:', updateData);
            
            const { error: updateError } = await supabase
              .from('scraped_properties')
              .update(updateData)
              .eq('id', property.id);

            if (updateError) {
              console.error('Error updating artwork details:', updateError);
            } else {
              console.log('Artwork details update successful for property:', property.id);
              
              // Immediately update the local state to show changes
              setProperties(prevProperties => {
                const updatedProperties = prevProperties.map(p => 
                  p.id === property.id 
                    ? {
                        ...p,
                        ...updateData
                      }
                    : p
                );
                console.log('Local state updated with artwork details:', updatedProperties.find(p => p.id === property.id));
                return updatedProperties;
              });
            }
          } else if (artworkData.success === false) {
            // Handle case where artwork was not generated due to low suitability score
            console.log('Artwork generation skipped due to low suitability score:', artworkData.data?.suitabilityScore);
            
            const updateData = {
              raw_data: { 
                ...property.raw_data, 
                artworkGeneration: {
                  suitabilityScore: artworkData.data?.suitabilityScore,
                  analysisResult: artworkData.data?.analysisResult,
                  sourceImageUrl: artworkData.data?.zillowImageUrl || artworkData.data?.redfinImageUrl,
                  skipped: true,
                  reason: artworkData.error,
                  address_used: addressForArtwork,
                  analyzed_at: new Date().toISOString()
                }
              },
            };
            
            const { error: updateError } = await supabase
              .from('scraped_properties')
              .update(updateData)
              .eq('id', property.id);

            if (!updateError) {
              // Update local state
              setProperties(prevProperties => 
                prevProperties.map(p => 
                  p.id === property.id 
                    ? { ...p, ...updateData }
                    : p
                )
              );
            }
          }
        } catch (error) {
          console.error(`Error generating artwork for property ${property.id}:`, error);
          
          // Store error in raw_data but don't mark as failed
          await supabase
            .from('scraped_properties')
            .update({
              raw_data: { 
                ...property.raw_data, 
                artworkGenerationError: error instanceof Error ? error.message : 'Unknown error' 
              }
            })
            .eq('id', property.id);
        }

        processed++;
        setArtworkProgress({ current: processed, total: selectedPropertiesArray.length });
      }

      // Reload the data to show updates
      console.log('Artwork generation completed, refreshing data...');
      await refreshPropertiesData();
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error generating artwork:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate artwork');
    } finally {
      setGeneratingArtwork(false);
      setArtworkProgress({ current: 0, total: 0 });
    }
  };

  const generateZillowWatercolorsForSelected = async () => {
    console.log('=== generateZillowWatercolorsForSelected CALLED ===');
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Only for under contract campaigns
    if (campaign?.campaign_subtype !== 'under_contract_sellers_agent') {
      setError('Watercolor generation is only available for under contract campaigns');
      return;
    }

    // Filter to only properties that have Zillow URLs and images
    const propertiesWithZillowImages = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => {
        if (!p) return false;
        const zillowImageUrl = p.zillow_image_url;
        return zillowImageUrl && zillowImageUrl.includes('zillowstatic.com');
      });

    if (propertiesWithZillowImages.length === 0) {
      setError('No properties with Zillow images selected. Please fetch Zillow details first.');
      return;
    }

    setGeneratingWatercolors(true);
    setWatercolorProgress({ current: 0, total: propertiesWithZillowImages.length });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Clear any previous messages
      setSuccessMessage(null);
      setError(null);

      let processed = 0;
      let successCount = 0;
      let skippedCount = 0;

      for (const property of propertiesWithZillowImages) {
        try {
          const zillowImageUrl = property.zillow_image_url;
          const address = `${property.address_line_1}, ${property.city}, ${property.state} ${property.zip_code}`.trim();
          
          console.log(`Generating watercolor for property ${property.id}: ${address}`);
          console.log(`Using Zillow image: ${zillowImageUrl}`);
          
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-zillow-artwork`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              zillowImageUrl: zillowImageUrl,
              address: address,
              propertyId: property.id,
              style: 'watercolor'
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const watercolorData = await response.json()
          console.log('Received watercolor data:', watercolorData)

          if (watercolorData.success && watercolorData.data.artworkUrl) {
            console.log(`✅ Watercolor generated successfully for property ${property.id}`)
            console.log(`Artwork URL: ${watercolorData.data.artworkUrl}`)
            successCount++;
            
            // Update local state immediately to show the new watercolor
            setProperties(prevProperties => 
              prevProperties.map(p => 
                p.id === property.id 
                  ? { 
                      ...p, 
                      watercolor_image_url: watercolorData.data.artworkUrl,
                      suitability_score: watercolorData.data.suitabilityScore,
                      suitability_analysis: watercolorData.data.analysisResult
                    }
                  : p
              )
            );
          } else if (watercolorData.success === false && watercolorData.error === 'Image not suitable for artwork generation') {
            console.log(`⚠️ Image not suitable for watercolor generation for property ${property.id} (score: ${watercolorData.data.suitabilityScore})`)
            skippedCount++;
          } else {
            console.error(`❌ Failed to generate watercolor for property ${property.id}:`, watercolorData.error)
            throw new Error(watercolorData.error || 'Unknown error occurred')
          }

        } catch (error) {
          console.error(`Error generating watercolor for property ${property.id}:`, error);
          skippedCount++;
        }

        processed++;
        setWatercolorProgress({ current: processed, total: propertiesWithZillowImages.length });
      }

      // Auto-refresh the data to show updates
      try {
        console.log('Watercolor generation completed, refreshing data...');
        await refreshPropertiesData();
        console.log('Auto-refresh completed successfully');
        
        // Show completion summary
        if (skippedCount > 0) {
          setSuccessMessage(`✅ Watercolors completed: ${successCount} generated, ${skippedCount} skipped (unsuitable images)`);
        } else {
          setSuccessMessage(`✅ Successfully generated ${successCount} watercolor artworks`);
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      } catch (refreshError) {
        console.error('Auto-refresh failed (non-critical):', refreshError);
        
        // Show success but mention that manual refresh may be needed
        if (skippedCount > 0) {
          setSuccessMessage(`✅ Watercolors completed: ${successCount} generated, ${skippedCount} skipped. Updates saved - refresh page if not visible.`);
        } else {
          setSuccessMessage(`✅ Successfully generated ${successCount} watercolor artworks. Updates saved - refresh page if not visible.`);
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000);
      }
      
      // Clear selection
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error('Error generating watercolors:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate watercolors');
    } finally {
      setGeneratingWatercolors(false);
      setWatercolorProgress({ current: 0, total: 0 });
    }
  };

  const sendAnniversaryEmailsForSelected = async () => {
    console.log('=== sendAnniversaryEmailsForSelected CALLED ===');
    console.log('selectedProperties.size:', selectedProperties.size);
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Determine campaign type to use correct email strategy
    const isRedfinCampaign = campaign?.source_platform === 'redfin' || 
                             campaign?.campaign_subtype === 'under_contract_sellers_agent';
    
    console.log('🎯 Email campaign type detection:', {
      sourcePlatform: campaign?.source_platform,
      campaignSubtype: campaign?.campaign_subtype,
      isRedfinCampaign,
      campaignType: isRedfinCampaign ? 'sellers_agent' : 'anniversary'
    });

    // Get selected properties that have artwork and agent contact details
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p)
      .filter(p => {
        // Must have artwork generated - prioritize dedicated columns, fallback to raw_data for existing data
        const hasArtwork = p.watercolor_image_url ||
                          p.oil_image_url ||
                          p.pencil_image_url ||
                          p.charcoal_image_url ||
                          p.raw_data?.artworkGeneration?.artworkUrl; // Fallback for existing data
        
        // Agent info requirements depend on campaign type
        let hasAgentInfo;
        if (isRedfinCampaign) {
          // For Redfin/under-contract campaigns, check sellers agent info
          hasAgentInfo = p.sellers_agent_name && p.sellers_agent_email;
        } else {
          // For anniversary campaigns, check buyers agent info
          hasAgentInfo = p.buyers_agent_name && p.buyers_agent_email;
        }
        
        // Must have a property address
        const hasAddress = p.address_line_1 || p.address || p.street_address;
        
        if (!hasArtwork) {
          console.log(`Property ${p.id} missing artwork:`, { 
            watercolorUrl: p.watercolor_image_url,
            oilUrl: p.oil_image_url,
            pencilUrl: p.pencil_image_url,
            charcoalUrl: p.charcoal_image_url,
            rawDataUrl: p.raw_data?.artworkGeneration?.artworkUrl // Fallback for existing data
          });
        }
        if (!hasAgentInfo) {
          const agentType = isRedfinCampaign ? 'sellers' : 'buyers';
          console.log(`Property ${p.id} missing ${agentType} agent info:`, { 
            sellersAgentName: p.sellers_agent_name,
            sellersAgentEmail: p.sellers_agent_email,
            buyersAgentName: p.buyers_agent_name, 
            buyersAgentEmail: p.buyers_agent_email 
          });
        }
        if (!hasAddress) {
          console.log(`Property ${p.id} missing address:`, { 
            address_line_1: p.address_line_1,
            address: p.address, 
            streetAddress: p.street_address 
          });
        }
        
        return hasArtwork && hasAgentInfo && hasAddress;
      });

    const emailType = isRedfinCampaign ? 'sellers agent' : 'anniversary';
    console.log(`Filtered properties for ${emailType} email:`, selectedPropertiesArray.length);

    if (selectedPropertiesArray.length === 0) {
      const agentType = isRedfinCampaign ? 'sellers agent' : 'buyers agent';
      setEmailError(`No properties found with artwork generated and ${agentType} contact details. Please ensure properties have generated artwork and ${agentType} information.`);
      setSendingEmails(false);
      return;
    }

    setEmailProgress({ current: 0, total: selectedPropertiesArray.length });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      for (let i = 0; i < selectedPropertiesArray.length; i++) {
        const property = selectedPropertiesArray[i];
        console.log(`Sending ${emailType} email ${i + 1}/${selectedPropertiesArray.length} for property:`, property.id);
        
        // Extract street address only (before first comma) - use address_line_1 from scraped_properties table
        const fullAddress = property.address_line_1 || property.address || property.street_address || '';
        const streetAddressOnly = fullAddress.split(',')[0].trim();
        
        console.log(`Using address for email: "${streetAddressOnly}" (from: ${property.address_line_1 ? 'address_line_1' : property.address ? 'address' : 'street_address'})`);
        
        // Get artwork URL - prioritize dedicated columns, fallback to raw_data for existing data
        const artworkUrl = property.watercolor_image_url ||
                          property.oil_image_url ||
                          property.pencil_image_url ||
                          property.charcoal_image_url ||
                          property.raw_data?.artworkGeneration?.artworkUrl; // Fallback for existing data
        
        // Generate configurator link using access code if available (direct URL like share functionality)
        const configuratorLink = property.access_code 
          ? propertyTokenGenerator.generateAccessCodeUrl(property.access_code)
          : `${window.location.origin}/property/${property.token || 'default'}?from=email`;
        
        // Prepare email data based on campaign type
        let emailData;
        let endpoint;
        let trackingEmailType;
        
        if (isRedfinCampaign) {
          // Sellers agent email for Redfin/under-contract campaigns
          emailData = {
            agentName: property.sellers_agent_name,
            agentEmail: property.sellers_agent_email,
            street: streetAddressOnly,
            artworkUrl: artworkUrl,
            configuratorLink: configuratorLink,
            accessCode: property.access_code || null
          };
          endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-sellers-agent-email`;
          trackingEmailType = 'sellers_agent_outreach';
        } else {
          // Anniversary email for regular campaigns
          emailData = {
          agentName: property.buyers_agent_name,
          street: streetAddressOnly,
            artworkUrl: artworkUrl,
            configuratorLink: configuratorLink,
            accessCode: property.access_code || null
        };
          endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-anniversary-email`;
          trackingEmailType = 'anniversary_outreach';
        }

        console.log(`Sending ${emailType} email with data:`, emailData);
        
        // Send the email via appropriate edge function
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'X-Admin-Token': adminToken,
          },
          body: JSON.stringify(emailData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const emailResult = await response.json();
        console.log(`${emailType} email sent successfully:`, emailResult);
        
        // 🎯 TRACK EMAIL SENT EVENT
        console.log(`🎯 Tracking ${emailType} email sent event...`);
        try {
          const recipientEmail = isRedfinCampaign ? property.sellers_agent_email : property.buyers_agent_email;
          const agentName = isRedfinCampaign ? property.sellers_agent_name : property.buyers_agent_name;
          
          await campaignTracking.trackEmailSent({
            propertyId: property.id,
            campaignId: property.campaign_id,
            accessCode: property.access_code,
            emailType: trackingEmailType,
            recipientEmail: recipientEmail,
            agentName: agentName
          });
          
          console.log(`✅ Successfully tracked ${emailType} email sent event`, {
            propertyId: property.id,
            campaignId: property.campaign_id,
            recipientEmail: recipientEmail,
            agentName: agentName
          });
        } catch (trackingError) {
          console.warn(`⚠️ Failed to track ${emailType} email sent event:`, trackingError);
          // Continue anyway - tracking shouldn't block the email process
        }
        
        // Update progress
        setEmailProgress({ current: i + 1, total: selectedPropertiesArray.length });
      }
      
      console.log(`All ${emailType} emails sent successfully!`);
      
      // Clear selection on success
      setSelectedProperties(new Set());
      
    } catch (error) {
      console.error(`Error sending ${emailType} emails:`, error);
      setEmailError(error instanceof Error ? error.message : `Failed to send ${emailType} emails`);
    } finally {
      setSendingEmails(false);
      setEmailProgress({ current: 0, total: 0 });
    }
  };

  const sendTestEmail = async () => {
    console.log('📧 TEST EMAIL - Starting test email process...');
    
    setSendingTestEmail(true);
    setEmailError(null);
    setTestEmailSuccess(null);

    // Initialize all variables at the start
    let testStreetAddress = "123 Demo Street, Test City, CA 90210";
    let artworkUrl = "https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/propertyimages/watercolor-1749478289092.jpg";
    let testConfiguratorLink = "https://framemyhome.com/configure";
    let selectedProperty = null;

    try {
      // Check admin authentication
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required - please log in again');
      }

      // Check environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Missing environment variables');
      }

      console.log('🔍 Checking selected properties...', selectedProperties.size);
      
      // Look for a property with artwork if any are selected
      if (selectedProperties.size > 0) {
        const selectedPropertiesArray = Array.from(selectedProperties)
          .map(id => properties.find(p => p.id === id))
          .filter(Boolean);
        
        console.log('📊 Found selected properties:', selectedPropertiesArray.length);
        
        // Find property with artwork
        for (const property of selectedPropertiesArray) {
          const hasArtwork = property?.watercolor_image_url ||
                            property?.oil_image_url ||
                            property?.pencil_image_url ||
                            property?.charcoal_image_url ||
                            property?.raw_data?.artworkGeneration?.artworkUrl;
          
          if (hasArtwork) {
            selectedProperty = property;
            
            // Use real artwork URL
            artworkUrl = property.watercolor_image_url ||
                        property.oil_image_url ||
                        property.pencil_image_url ||
                        property.charcoal_image_url ||
                        property.raw_data?.artworkGeneration?.artworkUrl;
            
            // Use real street address
            const realAddress = property.address_line_1 || property.address || property.street_address;
            if (realAddress) {
              testStreetAddress = realAddress;
      }

            // Use real access code if available
            if (property.access_code) {
              testConfiguratorLink = propertyTokenGenerator.generateAccessCodeUrl(property.access_code);
            }
            
            console.log('✅ Using data from property:', property.id);
            break;
          }
        }
      }

      console.log('📧 Email data:', {
        artworkUrl,
        testStreetAddress,
        testConfiguratorLink,
        hasSelectedProperty: !!selectedProperty
      });

      // Determine campaign type to use correct email endpoint
      const isRedfinCampaign = campaign?.source_platform === 'redfin' || 
                               campaign?.campaign_subtype === 'under_contract_sellers_agent';
      
      console.log('🎯 Campaign type detection:', {
        sourcePlatform: campaign?.source_platform,
        campaignSubtype: campaign?.campaign_subtype,
        isRedfinCampaign
      });

      let emailPayload;
      let endpoint;

      if (isRedfinCampaign) {
        // Use sellers agent email for Redfin/under-contract campaigns
        emailPayload = {
          isTestEmail: true,
          agentName: "Thomas Kleist",
          agentEmail: "test@example.com", // Required for sellers agent email
          street: testStreetAddress,
          artworkUrl: artworkUrl,
          configuratorLink: testConfiguratorLink,
          accessCode: selectedProperty?.access_code || null
        };
        
        endpoint = `${supabaseUrl}/functions/v1/send-sellers-agent-email`;
        console.log('📧 Using SELLERS AGENT email endpoint for Redfin/under-contract campaign');
        } else {
        // Use anniversary email for other campaigns
        emailPayload = {
          isTestEmail: true,
          agentName: "Thomas Kleist",
          street: testStreetAddress,
          artworkUrl: artworkUrl,
          configuratorLink: testConfiguratorLink,
          accessCode: selectedProperty?.access_code || null
        };
        
        endpoint = `${supabaseUrl}/functions/v1/send-anniversary-email`;
        console.log('📧 Using ANNIVERSARY email endpoint for regular campaign');
      }
      
      console.log('🎯 Calling endpoint:', endpoint);
      console.log('📦 Payload:', emailPayload);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'X-Admin-Token': adminToken,
        },
        body: JSON.stringify(emailPayload),
      });

      console.log('📬 Response status:', response.status);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('❌ Error response:', errorData);
        } catch (parseError) {
          errorData = { message: `HTTP error! status: ${response.status}` };
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Test email sent successfully:', result);
      
      const emailType = isRedfinCampaign ? 'sellers agent' : 'anniversary';
      setTestEmailSuccess(`Test ${emailType} email sent successfully to thomkleist@gmail.com! Check your inbox.`);
      
      setTimeout(() => {
        setTestEmailSuccess(null);
      }, 5000);
      
    } catch (error) {
      console.error('❌ Error sending test email:', error);
      setEmailError(error instanceof Error ? error.message : 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const fetchDetailsForLast60Properties = async () => {
    // Get the last 60 properties by creation date (most recent first)
    const last60Properties = properties
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 60)
      .filter(p => p.processing_status === 'pending'); // Only process pending properties

    if (last60Properties.length === 0) {
      setError('No pending properties found in the last 60 properties');
      return;
    }

    // Temporarily set selected properties to the last 60
    const last60PropertyIds = new Set(last60Properties.map(p => p.id));
    setSelectedProperties(last60PropertyIds);

    // Use the existing fetch details function
    await fetchDetailsForSelected();
  };

  const generateOilImagesForSelected = async () => {
    console.log('=== generateOilImagesForSelected CALLED ===');
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Get all selected properties that have source images
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p);

    console.log('selectedPropertiesArray:', selectedPropertiesArray.length);

    if (selectedPropertiesArray.length === 0) {
      console.log('No valid properties found, setting error');
      setError('No valid properties selected');
      return;
    }

    // Check if these are Zillow properties - if so, use the unified Zillow batch processing
    const sourcePlatform = campaign?.source_platform;
    if (sourcePlatform === 'zillow' || campaign?.campaign_subtype === 'under_contract_sellers_agent') {
      console.log('Zillow properties detected, using unified Zillow batch processing (all 3 styles)...');
      return await generateAllStylesForZillowProperties();
    }

    console.log('Starting oil image generation...');
    setGeneratingImages(true);
    setImageGenerationProgress({ 
      currentProperty: 0, 
      totalProperties: selectedPropertiesArray.length, 
      currentStyle: 'oil', 
      propertyAddress: '',
      totalImages: selectedPropertiesArray.length,
      successfulImages: 0
    });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Clear any previous messages
      setSuccessMessage(null);
      setError(null);
      setImageGenerationMessage(null);

      let processed = 0;
      let totalSuccessful = 0;
      let totalFailed = 0;
      const failedOperations: string[] = [];

      // Helper function to create address string
      const createAddressString = (property: any) => {
        const hasAddress = property.address_line_1 && typeof property.address_line_1 === 'string' && property.address_line_1.trim() !== '';
        const hasCity = property.city && typeof property.city === 'string' && property.city.trim() !== '';
        const hasState = property.state && typeof property.state === 'string' && property.state.trim() !== '';
        const hasZip = property.zip_code && typeof property.zip_code === 'string' && property.zip_code.trim() !== '';
        
        if (hasAddress || hasCity || hasState) {
          const addressParts = [];
          
          if (hasAddress) {
            addressParts.push(property.address_line_1.trim());
          }
          
          const locationParts = [];
          if (hasCity) locationParts.push(property.city.trim());
          if (hasState) locationParts.push(property.state.trim());
          if (hasZip) locationParts.push(property.zip_code.trim());
          
          if (locationParts.length > 0) {
            addressParts.push(locationParts.join(', '));
          }
          
          return addressParts.join(', ');
        } else {
          return `Property ${property.id}`;
        }
      };

      // Process properties in batches of 3 (same as Redfin artwork function)
      console.log(`🎨 Processing ${selectedPropertiesArray.length} properties in batches of 3...`);
      
      // Split into batches of 3
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < selectedPropertiesArray.length; i += batchSize) {
        batches.push(selectedPropertiesArray.slice(i, i + batchSize));
      }

      for (const [batchIndex, batch] of batches.entries()) {
        console.log(`🎨 Processing oil batch ${batchIndex + 1}/${batches.length} (${batch.length} properties)`);
        
        // Update progress
        setImageGenerationMessage(`Processing batch ${batchIndex + 1}/${batches.length} for oil images...`);
        
        try {
          // Process this batch of properties
          const batchPromises = batch.map(async (property) => {
            const propertyAddress = createAddressString(property);
            
            try {
              console.log(`Generating oil image for: ${propertyAddress}`);
              
              const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-campaign-images`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'X-Admin-Token': adminToken,
                },
                body: JSON.stringify({
                  propertyIds: [property.id],
                  style: 'oil',
                }),
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
              }

              const result = await response.json();
              console.log(`✅ Oil image completed for ${propertyAddress}:`, result);

              if (result.success && result.results.successful > 0) {
                console.log(`✅ Successfully generated oil image for ${propertyAddress}`);
                return { success: true, propertyAddress, property };
              } else {
                const errorMsg = result.error || 'Unknown error';
                console.error(`❌ Failed to generate oil image for ${propertyAddress}:`, errorMsg);
                return { success: false, propertyAddress, error: errorMsg, property };
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              console.error(`❌ Error generating oil image for ${propertyAddress}:`, errorMessage);
              return { success: false, propertyAddress, error: errorMessage, property };
            }
          });

          // Wait for all properties in this batch to complete
          const batchResults = await Promise.all(batchPromises);

          // Process results
          for (const result of batchResults) {
            if (result.success) {
              totalSuccessful++;
            } else {
              totalFailed++;
              failedOperations.push(`${result.propertyAddress}: ${result.error}`);
            }
            processed++;
            
            // Update progress
            setImageGenerationProgress(prev => ({
              ...prev,
              currentProperty: processed,
              propertyAddress: result.propertyAddress,
              successfulImages: totalSuccessful
            }));
          }

          console.log(`✅ Batch ${batchIndex + 1} completed: ${batchResults.filter(r => r.success).length}/${batch.length} successful`);

        } catch (error) {
          console.error(`❌ Error processing oil batch ${batchIndex + 1}:`, error);
          // Continue with next batch even if this one fails
          for (const property of batch) {
            totalFailed++;
            const propertyAddress = createAddressString(property);
            failedOperations.push(`${propertyAddress}: Batch processing error`);
            processed++;
          }
        }

        // Update progress message after each batch
        setImageGenerationMessage(`Batch ${batchIndex + 1}/${batches.length} completed. ${totalSuccessful} oil images generated, ${totalFailed} failed.`);
      }

      console.log(`🎉 All oil batches completed! Total: ${totalSuccessful} successful, ${totalFailed} failed`);

      // Show final results
      if (totalFailed > 0) {
        setImageGenerationMessage(`🎉 Oil images completed: ${totalSuccessful} generated, ${totalFailed} failed.`);
        if (failedOperations.length > 0) {
          setError(`Some oil image operations failed:\n${failedOperations.join('\n')}`);
        }
      } else {
        setImageGenerationMessage(`🎉 Success! Generated ${totalSuccessful} oil images for ${selectedPropertiesArray.length} properties.`);
        setError(null);
      }

      // Refresh the properties data to show the new images
      console.log('🔄 Refreshing property data...');
      setImageGenerationMessage(`🔄 Refreshing data to show new oil images...`);
      await refreshPropertiesData();

      // Clear selection after successful generation
      setSelectedProperties(new Set());

      // Show final success message
      setImageGenerationMessage(`✅ Oil image generation complete! Generated ${totalSuccessful} images. Data refreshed.`);
      
      // Clear the success message after 5 seconds
      setTimeout(() => {
        setImageGenerationMessage(null);
      }, 5000);

    } catch (error) {
      console.error('❌ Error in oil image generation:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate oil images');
    } finally {
      setGeneratingImages(false);
      setImageGenerationProgress({
        currentProperty: 0,
        totalProperties: 0,
        currentStyle: '',
        propertyAddress: '',
        totalImages: 0,
        successfulImages: 0
      });
    }
  };

  const generatePencilImagesForSelected = async () => {
    console.log('=== generatePencilImagesForSelected CALLED ===');
    
    if (selectedProperties.size === 0) {
      console.log('No properties selected, returning early');
      return;
    }

    // Get all selected properties that have source images
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p);

    console.log('selectedPropertiesArray:', selectedPropertiesArray.length);

    if (selectedPropertiesArray.length === 0) {
      console.log('No valid properties found, setting error');
      setError('No valid properties selected');
      return;
    }

    // Check if these are Zillow properties - if so, use the unified Zillow batch processing
    const sourcePlatform = campaign?.source_platform;
    if (sourcePlatform === 'zillow' || campaign?.campaign_subtype === 'under_contract_sellers_agent') {
      console.log('Zillow properties detected, using unified Zillow batch processing (all 3 styles)...');
      return await generateAllStylesForZillowProperties();
    }

    console.log('Starting pencil image generation...');
    setGeneratingImages(true);
    setImageGenerationProgress({ 
      currentProperty: 0, 
      totalProperties: selectedPropertiesArray.length, 
      currentStyle: 'pencil', 
      propertyAddress: '',
      totalImages: selectedPropertiesArray.length,
      successfulImages: 0
    });

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      // Clear any previous messages
      setSuccessMessage(null);
      setError(null);
      setImageGenerationMessage(null);

      let processed = 0;
      let totalSuccessful = 0;
      let totalFailed = 0;
      const failedOperations: string[] = [];

      // Helper function to create address string
      const createAddressString = (property: any) => {
        const hasAddress = property.address_line_1 && typeof property.address_line_1 === 'string' && property.address_line_1.trim() !== '';
        const hasCity = property.city && typeof property.city === 'string' && property.city.trim() !== '';
        const hasState = property.state && typeof property.state === 'string' && property.state.trim() !== '';
        const hasZip = property.zip_code && typeof property.zip_code === 'string' && property.zip_code.trim() !== '';
        
        if (hasAddress || hasCity || hasState) {
          const addressParts = [];
          
          if (hasAddress) {
            addressParts.push(property.address_line_1.trim());
          }
          
          const locationParts = [];
          if (hasCity) locationParts.push(property.city.trim());
          if (hasState) locationParts.push(property.state.trim());
          if (hasZip) locationParts.push(property.zip_code.trim());
          
          if (locationParts.length > 0) {
            addressParts.push(locationParts.join(', '));
          }
          
          return addressParts.join(', ');
        } else {
          return `Property ${property.id}`;
        }
      };

      // Process properties in batches of 3 (same as Redfin artwork function)
      console.log(`🎨 Processing ${selectedPropertiesArray.length} properties in batches of 3...`);
      
      // Split into batches of 3
      const batchSize = 3;
      const batches = [];
      for (let i = 0; i < selectedPropertiesArray.length; i += batchSize) {
        batches.push(selectedPropertiesArray.slice(i, i + batchSize));
      }

      for (const [batchIndex, batch] of batches.entries()) {
        console.log(`🎨 Processing pencil batch ${batchIndex + 1}/${batches.length} (${batch.length} properties)`);
        
        // Update progress
        setImageGenerationMessage(`Processing batch ${batchIndex + 1}/${batches.length} for pencil images...`);
        
        try {
          // Process this batch of properties
          const batchPromises = batch.map(async (property) => {
            const propertyAddress = createAddressString(property);
            
            try {
              console.log(`Generating pencil image for: ${propertyAddress}`);
              
              const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-campaign-images`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'X-Admin-Token': adminToken,
                },
                body: JSON.stringify({
                  propertyIds: [property.id],
                  style: 'pencil',
                }),
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
              }

              const result = await response.json();
              console.log(`✅ Pencil image completed for ${propertyAddress}:`, result);

              if (result.success && result.results.successful > 0) {
                console.log(`✅ Successfully generated pencil image for ${propertyAddress}`);
                return { success: true, propertyAddress, property };
              } else {
                const errorMsg = result.error || 'Unknown error';
                console.error(`❌ Failed to generate pencil image for ${propertyAddress}:`, errorMsg);
                return { success: false, propertyAddress, error: errorMsg, property };
              }
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Unknown error';
              console.error(`❌ Error generating pencil image for ${propertyAddress}:`, errorMessage);
              return { success: false, propertyAddress, error: errorMessage, property };
            }
          });

          // Wait for all properties in this batch to complete
          const batchResults = await Promise.all(batchPromises);

          // Process results
          for (const result of batchResults) {
            if (result.success) {
              totalSuccessful++;
            } else {
              totalFailed++;
              failedOperations.push(`${result.propertyAddress}: ${result.error}`);
            }
            processed++;
            
            // Update progress
            setImageGenerationProgress(prev => ({
              ...prev,
              currentProperty: processed,
              propertyAddress: result.propertyAddress,
              successfulImages: totalSuccessful
            }));
          }

          console.log(`✅ Batch ${batchIndex + 1} completed: ${batchResults.filter(r => r.success).length}/${batch.length} successful`);

        } catch (error) {
          console.error(`❌ Error processing pencil batch ${batchIndex + 1}:`, error);
          // Continue with next batch even if this one fails
          for (const property of batch) {
            totalFailed++;
            const propertyAddress = createAddressString(property);
            failedOperations.push(`${propertyAddress}: Batch processing error`);
            processed++;
          }
        }

        // Update progress message after each batch
        setImageGenerationMessage(`Batch ${batchIndex + 1}/${batches.length} completed. ${totalSuccessful} pencil images generated, ${totalFailed} failed.`);
      }

      console.log(`🎉 All pencil batches completed! Total: ${totalSuccessful} successful, ${totalFailed} failed`);

      // Show final results
      if (totalFailed > 0) {
        setImageGenerationMessage(`🎉 Pencil images completed: ${totalSuccessful} generated, ${totalFailed} failed.`);
        if (failedOperations.length > 0) {
          setError(`Some pencil image operations failed:\n${failedOperations.join('\n')}`);
        }
      } else {
        setImageGenerationMessage(`🎉 Success! Generated ${totalSuccessful} pencil images for ${selectedPropertiesArray.length} properties.`);
        setError(null);
      }

      // Refresh the properties data to show the new images
      console.log('🔄 Refreshing property data...');
      setImageGenerationMessage(`🔄 Refreshing data to show new pencil images...`);
      await refreshPropertiesData();

      // Clear selection after successful generation
      setSelectedProperties(new Set());

      // Show final success message
      setImageGenerationMessage(`✅ Pencil image generation complete! Generated ${totalSuccessful} images. Data refreshed.`);
      
      // Clear the success message after 5 seconds
      setTimeout(() => {
        setImageGenerationMessage(null);
      }, 5000);

    } catch (error) {
      console.error('❌ Error in pencil image generation:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate pencil images');
    } finally {
      setGeneratingImages(false);
      setImageGenerationProgress({
        currentProperty: 0,
        totalProperties: 0,
        currentStyle: '',
        propertyAddress: '',
        totalImages: 0,
        successfulImages: 0
      });
    }
  };

  const formatPrice = (priceInDollars: number | null) => {
    if (!priceInDollars) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(priceInDollars);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      // Processing statuses
      'pending': 'bg-gray-100 text-gray-600',
      'processing': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800',
      
      // Outreach statuses
      'sent': 'bg-blue-100 text-blue-800',
      'delivered': 'bg-green-100 text-green-800',
      'opened': 'bg-purple-100 text-purple-800',
      'clicked': 'bg-orange-100 text-orange-800',
      'responded': 'bg-emerald-100 text-emerald-800',
      'bounced': 'bg-red-100 text-red-800',
      'contacted': 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOutreachStatusDisplay = (property: ScrapedProperty) => {
    // Prioritize outreach status if it exists
    if (property.outreach_status) {
      return {
        status: property.outreach_status,
        label: property.outreach_status.charAt(0).toUpperCase() + property.outreach_status.slice(1),
        type: 'outreach'
      };
    }
    
    // Fall back to processing status
    return {
      status: property.processing_status,
      label: property.processing_status.charAt(0).toUpperCase() + property.processing_status.slice(1),
      type: 'processing'
    };
  };

  const getCampaignTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'closing_anniversary_agent': 'Closing Anniversary',
      'recently_closed_agent': 'Recently Closed',
      'recently_closed_customer': 'Customer Outreach',
      'under_contract_sellers_agent': 'Recently Sold (Seller Agents)',
    };
    return labels[type] || type;
  };

  const getSuitabilityScoreColor = (score: number | undefined) => {
    if (score === undefined || score === null) return 'bg-gray-100 text-gray-600';
    if (score >= 7) return 'bg-green-100 text-green-800';
    if (score >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  // QR Code generation function
  const generateQRCodesForSelected = async () => {
    console.log('=== generateQRCodesForSelected CALLED ===');
    
    if (generatingQRCodes) {
      console.log('Already generating QR codes, returning');
      return;
    }

    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p && p.access_code); // Only properties with access codes

    if (selectedPropertiesArray.length === 0) {
      setQRCodeMessage('❌ No selected properties with access codes found. Generate access codes first.');
      setTimeout(() => setQRCodeMessage(null), 5000);
      return;
    }

    console.log(`Found ${selectedPropertiesArray.length} selected properties with access codes`);

    setGeneratingQRCodes(true);
    setQRCodeProgress({ current: 0, total: selectedPropertiesArray.length });
    setQRCodeMessage('🎯 Generating QR codes...');

    try {
      const adminToken = adminAuth.getApiToken();
      if (!adminToken) {
        throw new Error('Admin authentication required');
      }

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < selectedPropertiesArray.length; i++) {
        const property = selectedPropertiesArray[i];
        console.log(`Generating QR code ${i + 1}/${selectedPropertiesArray.length} for property:`, property.id);
        
        try {
          // Generate QR code URL using framemyhome.ai format
          const qrCodeUrl = `https://framemyhome.ai/${property.access_code}`;
          console.log('QR code URL:', qrCodeUrl);

          // Call Edge Function to generate and store QR code
          const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-qr-code`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'X-Admin-Token': adminToken,
            },
            body: JSON.stringify({
              propertyId: property.id,
              accessCode: property.access_code,
              qrCodeUrl: qrCodeUrl
            }),
          });

          const data = await response.json();
          
          if (data.success) {
            console.log(`✅ QR code generated for property ${property.id}:`, data.qrCodeImageUrl);
            successCount++;
          } else {
            console.error(`❌ QR code generation failed for property ${property.id}:`, data.error);
            errorCount++;
          }
        } catch (error) {
          console.error(`❌ QR code generation failed for property ${property.id}:`, error);
          errorCount++;
        }

        setQRCodeProgress({ current: i + 1, total: selectedPropertiesArray.length });
      }

      // Final message
      if (successCount > 0) {
        setQRCodeMessage(`🎉 Successfully generated ${successCount} QR codes! ${errorCount > 0 ? `${errorCount} failed.` : ''}`);
        // Refresh the data to show updated QR codes
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setQRCodeMessage(`❌ Failed to generate QR codes. Check console for details.`);
      }

    } catch (error) {
      console.error('QR code generation error:', error);
      setQRCodeMessage(`❌ QR code generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setGeneratingQRCodes(false);
      setTimeout(() => {
        setQRCodeMessage(null);
        setQRCodeProgress({ current: 0, total: 0 });
      }, 5000);
    }
  };

  // CSV Export function
  const exportSelectedToCSV = () => {
    console.log('=== exportSelectedToCSV CALLED ===');
    
    const selectedPropertiesArray = Array.from(selectedProperties)
      .map(id => properties.find(p => p.id === id))
      .filter(p => p);

    if (selectedPropertiesArray.length === 0) {
      alert('Please select properties to export');
      return;
    }

    console.log(`Exporting ${selectedPropertiesArray.length} selected properties to CSV`);

    // CSV Headers
    const headers = [
      'Name',
      'address_line1', 
      'address_city',
      'address_state', 
      'address_zip',
      'qr_code_image_url',
      'qr_code_link',
      'watercolor_artwork_url',
      'pencil_artwork_url',
      'back_background_url',
      'front_background_url'
    ];

    // Static values as per your example
    const backBackgroundUrl = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates/Postcard_new_back_test3.png';
    const frontBackgroundUrl = 'https://qrmzfkvnksglemsiaxnp.supabase.co/storage/v1/object/public/Templates/Postcard_new_front_test3.png';

    // Get the first property to use its assets for header addresses
    const firstProperty = selectedPropertiesArray[0];
    const firstPropertyQrCodeLink = firstProperty?.access_code ? `framemyhome.ai/${firstProperty.access_code}` : '';
    
    // Create the two header addresses with sample data using first property's assets
    const headerAddresses = [
      [
        'Simon Berg', // Name
        '2 Chapel Hill Road', // address_line1
        'Sherman', // address_city
        'CT', // address_state
        '"06784"', // address_zip (quoted to preserve leading zeros)
        firstProperty?.qr_code_url || '', // Use first property's QR code image
        firstPropertyQrCodeLink, // Use first property's QR code link
        firstProperty?.watercolor_image_url || '', // Use first property's watercolor
        firstProperty?.pencil_image_url || '', // Use first property's pencil artwork
        backBackgroundUrl, // Static back background
        frontBackgroundUrl // Static front background
      ],
      [
        'Thomas Kleist', // Name
        '9 Boxwood St', // address_line1
        'East Hampton', // address_city
        'NY', // address_state
        '"11937"', // address_zip (quoted to preserve leading zeros)
        firstProperty?.qr_code_url || '', // Use first property's QR code image
        firstPropertyQrCodeLink, // Use first property's QR code link
        firstProperty?.watercolor_image_url || '', // Use first property's watercolor
        firstProperty?.pencil_image_url || '', // Use first property's pencil artwork
        backBackgroundUrl, // Static back background
        frontBackgroundUrl // Static front background
      ]
    ];

    // Generate CSV rows for selected properties
    const csvRows = selectedPropertiesArray.map(property => {
      // Generate QR code link using access code
      const qrCodeLink = property.access_code ? `framemyhome.ai/${property.access_code}` : '';
      
      // Ensure zip code preserves leading zeros
      const zipCode = property.zip_code ? String(property.zip_code).padStart(5, '0') : '';
      
      // Debug logging for zip code formatting
      if (property.zip_code) {
        console.log(`ZIP CODE DEBUG: Original: "${property.zip_code}" (${typeof property.zip_code}), Formatted: "${zipCode}"`);
      }
      
      return [
        'Home Owner', // Static name
        property.address_line_1 || '',
        property.city || '',
        property.state || '',
        zipCode, // Formatted zip code with leading zeros
        property.qr_code_url || '', // QR code image URL from storage
        qrCodeLink, // QR code link using access code
        property.watercolor_image_url || '', // Watercolor artwork URL
        property.pencil_image_url || '', // Pencil artwork URL
        backBackgroundUrl, // Static back background
        frontBackgroundUrl // Static front background
      ];
    });

    // Combine headers, header addresses, and data
    const csvContent = [headers, ...headerAddresses, ...csvRows]
      .map((row, rowIndex) => row.map((field, fieldIndex) => {
        // Always quote zip codes (field index 4) to preserve leading zeros, but skip header addresses that are already quoted
        if (rowIndex > 2 && fieldIndex === 4) { // Start from index 3 (after headers + 2 header addresses)
          return `"${field}"`;
        }
        // Header addresses (rows 1 and 2) already have proper quoting, so return as-is
        if (rowIndex <= 2 && fieldIndex === 4) {
          return field; // Already quoted in headerAddresses
        }
        // Escape fields that contain commas, quotes, or newlines
        if (typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      }).join(','))
      .join('\n');

    // Debug: Log a sample of the CSV content to check zip code formatting
    console.log('CSV Content Sample (first 3 lines):');
    console.log(csvContent.split('\n').slice(0, 3).join('\n'));

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      // Generate filename with campaign name and timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const campaignName = campaign?.name || 'campaign';
      const filename = `${campaignName.replace(/[^a-zA-Z0-9]/g, '_')}_export_${timestamp}.csv`;
      
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`✅ CSV exported: ${filename} with ${selectedPropertiesArray.length} properties`);
    }
  };

  const getSuitabilityScoreDisplay = (property: ScrapedProperty) => {
    // Debug logging
    if (property.id && property.redfin_main_image_url) {
      console.log(`🔍 [${property.id}] Checking suitability score:`, {
        id: property.id,
        suitability_score: property.suitability_score,
        suitability_analysis: property.suitability_analysis,
        raw_data_score: property.raw_data?.artworkGeneration?.suitabilityScore,
        has_redfin_image: !!property.redfin_main_image_url
      });
    }
    
    // First check the direct database field
    if (property.suitability_score !== undefined && property.suitability_score !== null) {
      return {
        score: property.suitability_score,
        source: 'database',
        hasZillowImage: !!property.zillow_image_url
      };
    }
    
    // Fallback to raw_data for backward compatibility
    if (property.raw_data?.artworkGeneration?.suitabilityScore !== undefined) {
      return {
        score: property.raw_data.artworkGeneration.suitabilityScore,
        source: 'raw_data',
        hasZillowImage: !!property.raw_data.artworkGeneration.zillowImageUrl
      };
    }
    
    return {
      score: undefined,
      source: 'none',
      hasZillowImage: false
    };
  };

  const getTrackingFunnelStatus = (property: ScrapedProperty) => {
    // Check for purchase first (highest funnel level)
    if (property.purchase_completed_at) {
      return {
        status: 'purchased',
        label: 'Purchased',
        color: 'bg-green-100 text-green-800'
      };
    }
    
    // Check for Stripe/payment access
    if (property.payment_page_accessed_count && property.payment_page_accessed_count > 0) {
      return {
        status: 'stripe_accessed',
        label: 'Stripe Accessed',
        color: 'bg-indigo-100 text-indigo-800'
      };
    }
    
    // Check for checkout access
    if (property.checkout_initiated_count && property.checkout_initiated_count > 0) {
      return {
        status: 'checkout_accessed',
        label: 'Checkout Accessed',
        color: 'bg-purple-100 text-purple-800'
      };
    }
    
    // Check for order summary access
    if (property.order_summary_accessed_count && property.order_summary_accessed_count > 0) {
      return {
        status: 'order_summary_accessed',
        label: 'Order Summary Accessed',
        color: 'bg-yellow-100 text-yellow-800'
      };
    }
    
    // Check for property opened (configurator access)
    if (property.configurator_opened_count && property.configurator_opened_count > 0) {
      return {
        status: 'configurator_accessed',
        label: 'Configurator Accessed',
        color: 'bg-orange-100 text-orange-800'
      };
    }
    
    // Check for landing page accessed
    if (property.landing_page_accessed_count && property.landing_page_accessed_count > 0) {
      return {
        status: 'landing_page_accessed',
        label: 'Landing Page Accessed',
        color: 'bg-blue-100 text-blue-800'
      };
    }
    
    // Check for email sent
    if (property.outreach_status === 'sent' || property.outreach_status === 'delivered' || property.outreach_status === 'opened' || property.outreach_status === 'clicked') {
      return {
        status: 'email_sent',
        label: 'Email Sent',
        color: 'bg-blue-100 text-blue-800'
      };
    }
    
    // Check if ready to send (has artwork and agent email)
    const hasArtwork = property.watercolor_image_url ||
                      property.oil_image_url ||
                      property.pencil_image_url ||
                      property.charcoal_image_url ||
                      property.raw_data?.artworkGeneration?.artworkUrl;
    
    const hasAgentEmail = property.buyers_agent_email && property.buyers_agent_email.trim() !== '';
    
    if (hasArtwork && hasAgentEmail) {
      return {
        status: 'ready_to_send',
        label: 'Ready to Send',
        color: 'bg-yellow-100 text-yellow-800'
      };
    }
    
    // Default fallback
    return {
      status: 'not_ready',
      label: 'Not Ready',
      color: 'bg-gray-100 text-gray-800'
    };
  };

  // Filter and sort properties
  const filteredAndSortedProperties = properties
    .filter(property => {
      const matchesSearch = searchTerm === '' || 
        property.address_line_1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.buyers_agent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.buyers_agent_brokerage?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || property.processing_status === statusFilter;
      
      // Tab-based filtering
      let matchesTab = true;
      if (activeTab === 'with_emails') {
        // Filter for properties that have agent emails
        if (campaign?.campaign_subtype === 'under_contract_sellers_agent') {
          matchesTab = property.sellers_agent_email && 
                      property.sellers_agent_email.trim() !== '' && 
                      property.sellers_agent_email.trim() !== 'null';
        } else {
          matchesTab = property.buyers_agent_email && 
                      property.buyers_agent_email.trim() !== '' && 
                      property.buyers_agent_email.trim() !== 'null';
        }
      } else if (activeTab === 'ready_to_send') {
        // Filter for properties that have both agent emails AND artwork
        let hasAgentEmail;
        if (campaign?.campaign_subtype === 'under_contract_sellers_agent') {
          hasAgentEmail = property.sellers_agent_email && 
                         property.sellers_agent_email.trim() !== '' && 
                         property.sellers_agent_email.trim() !== 'null';
        } else {
          hasAgentEmail = property.buyers_agent_email && 
                         property.buyers_agent_email.trim() !== '' && 
                         property.buyers_agent_email.trim() !== 'null';
        }
        
        // Check for artwork
        const hasArtwork = property.watercolor_image_url ||
                          property.oil_image_url ||
                          property.pencil_image_url ||
                          property.charcoal_image_url ||
                          property.raw_data?.artworkGeneration?.artworkUrl;
        
        matchesTab = hasAgentEmail && hasArtwork;
      }
      
      return matchesSearch && matchesStatus && matchesTab;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortBy) {
        case 'date_sold':
          aValue = a.date_sold ? new Date(a.date_sold).getTime() : 0;
          bValue = b.date_sold ? new Date(b.date_sold).getTime() : 0;
          break;
        case 'sale_price':
          aValue = a.sale_price || 0;
          bValue = b.sale_price || 0;
          break;
        case 'address':
          aValue = a.address_line_1 || '';
          bValue = b.address_line_1 || '';
          break;
        case 'suitability':
          const suitabilityA = getSuitabilityScoreDisplay(a).score;
          const suitabilityB = getSuitabilityScoreDisplay(b).score;
          if (suitabilityA === undefined && suitabilityB === undefined) return 0;
          if (suitabilityA === undefined) return sortOrder === 'asc' ? 1 : -1;
          if (suitabilityB === undefined) return sortOrder === 'asc' ? -1 : 1;
          aValue = suitabilityA || 0;
          bValue = suitabilityB || 0;
          break;
        case 'import_order':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Apply pagination
  const totalProperties = filteredAndSortedProperties.length;
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(totalProperties / itemsPerPage);
  
  // Reset to page 1 if current page is beyond total pages
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }
  
  let finalFilteredProperties: ScrapedProperty[];
  let startIndex: number;
  let endIndex: number;
  
  if (itemsPerPage === 'all') {
    finalFilteredProperties = filteredAndSortedProperties;
    startIndex = 0;
    endIndex = totalProperties;
  } else {
    startIndex = (currentPage - 1) * itemsPerPage;
    endIndex = Math.min(startIndex + itemsPerPage, totalProperties);
    finalFilteredProperties = filteredAndSortedProperties.slice(startIndex, endIndex);
  }

  // Remove excessive debug logging and just keep essential info
  const propertiesWithAddresses = properties.filter(p => p.address_line_1 && p.address_line_1.trim() !== '');
  
  const allSelected = finalFilteredProperties.length > 0 && 
    finalFilteredProperties.every(p => selectedProperties.has(p.id));
  const someSelected = finalFilteredProperties.some(p => selectedProperties.has(p.id));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Campaign</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/campaigns')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Campaign not found</p>
          <button
            onClick={() => navigate('/admin/campaigns')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  // Debug function - can be called from browser console
  (window as any).debugPropertyTracking = async (propertyId?: string) => {
    console.log('🔍 Debugging property tracking data...');
    
    if (propertyId) {
      // Debug specific property
      const { data, error } = await supabase
        .from('scraped_properties')
        .select('*')
        .eq('id', propertyId)
        .single();
      
      if (error) {
        console.error('Error fetching property:', error);
      } else {
        console.log('📊 Property tracking data:', {
          id: data.id,
          address: data.address_line_1,
          outreach_status: data.outreach_status,
          configurator_opened_count: data.configurator_opened_count,
          configurator_opened_at: data.configurator_opened_at,
          checkout_initiated_count: data.checkout_initiated_count,
          payment_page_accessed_count: data.payment_page_accessed_count,
          last_activity_at: data.last_activity_at,
          updated_at: data.updated_at,
          access_code: data.access_code
        });
      }
    } else {
      // Debug all properties in campaign
      const { data, error } = await supabase
        .from('scraped_properties')
        .select('id, address_line_1, outreach_status, configurator_opened_count, configurator_opened_at, access_code')
        .eq('campaign_id', campaignId);
      
      if (error) {
        console.error('Error fetching properties:', error);
      } else {
        console.log('📊 All properties tracking data:', data);
        const openedProperties = data.filter(p => p.configurator_opened_count && p.configurator_opened_count > 0);
        console.log(`🟠 Found ${openedProperties.length} opened properties:`, openedProperties);
      }
    }
  };

  // Add to console log so user knows about debug function
  console.log('🛠️ Debug function available: window.debugPropertyTracking(propertyId?) - Call with no args to see all properties, or with propertyId to see specific property');

  // Reset functions for tracking data
  const resetEmailSentStatus = async () => {
    if (!campaign?.id) return;

    try {
      setResettingEmails(true);
      setSuccessMessage(null); // Clear any existing success messages
      
      // Get properties that have email sent status
      const emailSentProperties = finalFilteredProperties.filter(p => 
        p.outreach_status === 'sent' || p.outreach_status === 'delivered' || 
        p.outreach_status === 'opened' || p.outreach_status === 'clicked'
      );

      if (emailSentProperties.length === 0) {
        setSuccessMessage('No properties with email sent status found.');
        return;
      }

      // Reset outreach status and sent date for these properties
      const { error } = await supabase
        .from('scraped_properties')
        .update({
          outreach_status: null,
          outreach_sent_at: null
        })
        .in('id', emailSentProperties.map(p => p.id));

      if (error) {
        throw error;
      }

      setSuccessMessage(`Reset email status for ${emailSentProperties.length} properties.`);
      
      // Refresh the data
      await refreshPropertiesData();
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error resetting email status:', error);
      setError(error instanceof Error ? error.message : 'Failed to reset email status');
    } finally {
      setResettingEmails(false);
      setShowResetEmailConfirm(false);
    }
  };

  const resetPropertyOpenedStatus = async () => {
    if (!campaign?.id) return;

    try {
      setResettingOpened(true);
      setSuccessMessage(null); // Clear any existing success messages
      
      // Get properties that have been opened (configurator accessed)
      const openedProperties = finalFilteredProperties.filter(p => 
        p.configurator_opened_count && p.configurator_opened_count > 0
      );

      if (openedProperties.length === 0) {
        setSuccessMessage('No properties with opened status found.');
        return;
      }

      // Reset configurator tracking for these properties
      const { error } = await supabase
        .from('scraped_properties')
        .update({
          configurator_opened_count: 0,
          configurator_opened_at: null
        })
        .in('id', openedProperties.map(p => p.id));

      if (error) {
        throw error;
      }

      setSuccessMessage(`Reset opened status for ${openedProperties.length} properties.`);
      
      // Refresh the data
      await refreshPropertiesData();
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (error) {
      console.error('Error resetting opened status:', error);
      setError(error instanceof Error ? error.message : 'Failed to reset opened status');
    } finally {
      setResettingOpened(false);
      setShowResetOpenedConfirm(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/campaigns')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Campaigns
              </button>
              <button
                onClick={refreshPropertiesData}
                disabled={refreshing}
                className="flex items-center text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                title="Refresh properties data"
              >
                <svg 
                  className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
                <p className="text-sm text-gray-600">
                  {getCampaignTypeLabel(campaign.campaign_subtype)} • {properties.length} properties
                </p>
              </div>
            </div>
            
            {campaign.screenshot_thumbnail_url && (
              <div className="flex-shrink-0">
                <img
                  src={campaign.screenshot_thumbnail_url}
                  alt="Campaign screenshot"
                  className="w-16 h-16 rounded-lg object-cover border"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-6 pb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All Properties
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                {properties.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('with_emails')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'with_emails'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Properties with Emails
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                {properties.filter(p => {
                  // Check for agent email in appropriate field based on campaign type
                  if (campaign?.campaign_subtype === 'under_contract_sellers_agent') {
                    return p.sellers_agent_email && 
                           p.sellers_agent_email.trim() !== '' && 
                           p.sellers_agent_email.trim() !== 'null';
                  } else {
                    return p.buyers_agent_email && 
                           p.buyers_agent_email.trim() !== '' && 
                           p.buyers_agent_email.trim() !== 'null';
                  }
                }).length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('ready_to_send')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'ready_to_send'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Ready to Send
              <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-gray-100 text-gray-900">
                {properties.filter(p => {
                  // Check for agent email in appropriate field based on campaign type
                  let hasAgentEmail;
                  if (campaign?.campaign_subtype === 'under_contract_sellers_agent') {
                    hasAgentEmail = p.sellers_agent_email && 
                                   p.sellers_agent_email.trim() !== '' && 
                                   p.sellers_agent_email.trim() !== 'null';
                  } else {
                    hasAgentEmail = p.buyers_agent_email && 
                                   p.buyers_agent_email.trim() !== '' && 
                                   p.buyers_agent_email.trim() !== 'null';
                  }
                  
                  // Check for artwork
                  const hasArtwork = p.watercolor_image_url ||
                                    p.oil_image_url ||
                                    p.pencil_image_url ||
                                    p.charcoal_image_url ||
                                    p.raw_data?.artworkGeneration?.artworkUrl;
                  
                  return hasAgentEmail && hasArtwork;
                }).length}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="contacted">Contacted</option>
              </select>
              
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                  setItemsPerPage(value);
                  setCurrentPage(1); // Reset to first page when changing items per page
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value="all">All properties</option>
              </select>
              
              {itemsPerPage !== 'all' && totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
              
              {/* Refresh button for view filters */}
              {false && (
                <button
                  onClick={() => setPersistedFilteredIds([])}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  title="Refresh the filtered list based on current data"
                >
                  <RefreshCw className="w-4 h-4" />
                  </button>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Selection Actions */}
              {selectedProperties.size > 0 && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 rounded-lg">
                  <span className="text-sm text-blue-700">
                    {selectedProperties.size} selected
                  </span>
                  
                  {/* Only show Fetch Details button for non-under-contract campaigns */}
                  {campaign?.campaign_subtype !== 'under_contract_sellers_agent' && (
                  <button
                    onClick={fetchDetailsForSelected}
                    disabled={fetchingDetails}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {fetchingDetails ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    <span>
                      {fetchingDetails ? 'Fetching...' : 'Fetch Details'}
                    </span>
                  </button>
                  )}
                  
                  {(() => {
                    // Check if any selected properties have agent information
                    // Skip for under contract campaigns since agent details are integrated into Zillow fetch
                    const isUnderContractCampaign = campaign?.campaign_subtype === 'under_contract_sellers_agent';
                    if (isUnderContractCampaign) return null;
                    
                    const selectedPropertiesWithAgents = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p && p.buyers_agent_name && p.buyers_agent_name.trim() !== '');
                    
                    if (selectedPropertiesWithAgents.length > 0) {
                      return (
                        <button
                          onClick={fetchAgentDetailsForSelected}
                          disabled={fetchingAgentDetails}
                          className="flex items-center space-x-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          {fetchingAgentDetails ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <User className="w-4 h-4" />
                          )}
                          <span>
                            {fetchingAgentDetails ? 'Finding Contacts...' : `Get Agent Details (${selectedPropertiesWithAgents.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // For under contract campaigns, show Fetch Details + Agent Contacts button
                    const isUnderContractCampaign = campaign?.campaign_subtype === 'under_contract_sellers_agent';
                    if (!isUnderContractCampaign) return null;
                    
                    const selectedPropertiesWithZillowUrls = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => {
                        if (!p) return false;
                        const zillowUrl = p.raw_data?.url || p.zillow_url;
                        return zillowUrl && zillowUrl.includes('zillow.com');
                      });
                    
                    if (selectedPropertiesWithZillowUrls.length > 0) {
                      return (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={fetchZillowDetailsForSelected}
                            disabled={fetchingDetails}
                            className="flex items-center space-x-2 bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                          >
                            {fetchingDetails ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                            <span>
                              {fetchingDetails ? 'Fetching Details + Contacts...' : `Fetch Details + Agent Contacts (${selectedPropertiesWithZillowUrls.length})`}
                            </span>
                          </button>
                          
                          <button
                            onClick={generateZillowWatercolorsForSelected}
                            disabled={generatingWatercolors}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                          >
                            {generatingWatercolors ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <div className="w-4 h-4">🎨</div>
                            )}
                            <span>
                              {generatingWatercolors ? 'Generating Watercolors...' : `Generate Watercolors (${selectedPropertiesWithZillowUrls.length})`}
                            </span>
                          </button>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Show Generate Artwork button for any selected properties
                    const selectedPropertiesArray = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p); // Just filter out undefined properties
                    
                    if (selectedPropertiesArray.length > 0) {
                      return (
                        <button
                          onClick={async () => {
                            console.log('=== Generate Artwork for Selected Button Clicked ===');
                            
                            if (generatingArtwork) {
                              console.log('Already generating artwork, returning');
                              return;
                            }
                            
                            console.log(`Found ${selectedPropertiesArray.length} selected properties`);
                            
                            // Call generation function directly
                            console.log('Calling generateArtworkForSelected...');
                            generateArtworkForSelected();
                          }}
                          disabled={generatingArtwork}
                          className="flex items-center space-x-2 bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                        >
                          {generatingArtwork ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          <span>
                            {generatingArtwork ? 'Generating...' : `Generate Artwork (${selectedPropertiesArray.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Show Generate QR Codes button for selected properties with access codes
                    const selectedPropertiesWithAccessCodes = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p && p.access_code);
                    
                    if (selectedPropertiesWithAccessCodes.length > 0) {
                      return (
                        <button
                          onClick={generateQRCodesForSelected}
                          disabled={generatingQRCodes}
                          className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {generatingQRCodes ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <QrCode className="w-4 h-4" />
                          )}
                          <span>
                            {generatingQRCodes ? 'Generating...' : `Generate QR Codes (${selectedPropertiesWithAccessCodes.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Show Generate Oil Images button for selected properties
                    const selectedPropertiesArray = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p); // Filter out undefined properties
                    
                    // Only show for properties that need oil images
                    const propertiesNeedingOilImages = selectedPropertiesArray.filter(p => !p.oil_image_url);
                    
                    if (propertiesNeedingOilImages.length > 0) {
                      return (
                        <button
                          onClick={generateOilImagesForSelected}
                          disabled={generatingImages}
                          className="flex items-center space-x-2 bg-amber-600 text-white px-3 py-1 rounded text-sm hover:bg-amber-700 disabled:opacity-50"
                        >
                          {generatingImages ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          <span>
                            {generatingImages ? 'Generating...' : `Generate Oil Images (${propertiesNeedingOilImages.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Show Generate Pencil Images button for selected properties
                    const selectedPropertiesArray = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p); // Filter out undefined properties
                    
                    // Only show for properties that need pencil images
                    const propertiesNeedingPencilImages = selectedPropertiesArray.filter(p => !p.pencil_image_url);
                    
                    if (propertiesNeedingPencilImages.length > 0) {
                      return (
                        <button
                          onClick={generatePencilImagesForSelected}
                          disabled={generatingImages}
                          className="flex items-center space-x-2 bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 disabled:opacity-50"
                        >
                          {generatingImages ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          )}
                          <span>
                            {generatingImages ? 'Generating...' : `Generate Pencil Images (${propertiesNeedingPencilImages.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}

                  {(() => {
                    // Show CSV Export button for any selected properties
                    const selectedPropertiesArray = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p);
                    
                    if (selectedPropertiesArray.length > 0) {
                      return (
                        <button
                          onClick={exportSelectedToCSV}
                          className="flex items-center space-x-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          <FileDown className="w-4 h-4" />
                          <span>
                            Export CSV ({selectedPropertiesArray.length})
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}


                  {(() => {
                    // Show Send Email button for properties with artwork and agent contact details
                    const selectedPropertiesWithArtworkAndAgents = Array.from(selectedProperties)
                      .map(id => properties.find(p => p.id === id))
                      .filter(p => p)
                      .filter(p => {
                        // Must have artwork generated - prioritize dedicated columns, fallback to raw_data for existing data
                        const hasArtwork = p.watercolor_image_url ||
                                          p.oil_image_url ||
                                          p.pencil_image_url ||
                                          p.charcoal_image_url ||
                                          p.raw_data?.artworkGeneration?.artworkUrl; // Fallback for existing data
                        
                        // Check for appropriate agent info based on campaign type
                        const isRedfinCampaign = campaign?.source_platform === 'redfin' || 
                                                campaign?.campaign_subtype === 'under_contract_sellers_agent';
                        
                        let hasAgentInfo;
                        if (isRedfinCampaign) {
                          // For Redfin/under-contract campaigns, check seller agent info
                          hasAgentInfo = p.sellers_agent_name && p.sellers_agent_email;
                        } else {
                          // For other campaigns, check buyer agent info
                          hasAgentInfo = p.buyers_agent_name && p.buyers_agent_email;
                        }
                        
                        // Must have a property address
                        const hasAddress = p.address_line_1 || p.address || p.street_address;
                        
                        if (!hasArtwork) {
                          console.log(`Property ${p.id} missing artwork:`, { 
                            watercolorUrl: p.watercolor_image_url,
                            oilUrl: p.oil_image_url,
                            pencilUrl: p.pencil_image_url,
                            charcoalUrl: p.charcoal_image_url,
                            rawDataUrl: p.raw_data?.artworkGeneration?.artworkUrl // Fallback for existing data
                          });
                        }
                        if (!hasAgentInfo) {
                          const agentType = isRedfinCampaign ? 'seller' : 'buyer';
                          console.log(`Property ${p.id} missing ${agentType} agent info:`, { 
                            sellersAgentName: p.sellers_agent_name,
                            sellersAgentEmail: p.sellers_agent_email,
                            buyersAgentName: p.buyers_agent_name, 
                            buyersAgentEmail: p.buyers_agent_email 
                          });
                        }
                        if (!hasAddress) {
                          console.log(`Property ${p.id} missing address:`, { 
                            address_line_1: p.address_line_1,
                            address: p.address, 
                            streetAddress: p.street_address 
                          });
                        }
                        
                        return hasArtwork && hasAgentInfo && hasAddress;
                      });
                    
                    if (selectedPropertiesWithArtworkAndAgents.length > 0) {
                      // Determine email type based on campaign
                      const isRedfinCampaign = campaign?.source_platform === 'redfin' || 
                                              campaign?.campaign_subtype === 'under_contract_sellers_agent';
                      const emailType = isRedfinCampaign ? 'Sellers Agent' : 'Anniversary';
                      
                      return (
                        <button
                          onClick={sendAnniversaryEmailsForSelected}
                          disabled={sendingEmails}
                          className="flex items-center space-x-2 bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 disabled:opacity-50"
                        >
                          {sendingEmails ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          <span>
                            {sendingEmails ? 'Sending...' : `Send ${emailType} Email (${selectedPropertiesWithArtworkAndAgents.length})`}
                          </span>
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
              
              {/* Send Test Email Button */}
              <button
                onClick={sendTestEmail}
                disabled={sendingTestEmail}
                className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                title="Send test email to thomas@mshstudio.ai"
              >
                {sendingTestEmail ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span>{sendingTestEmail ? 'Sending...' : 'Send Test Email'}</span>
              </button>

              {/* Refresh Button */}
              <button
                onClick={refreshPropertiesData}
                disabled={refreshing}
                className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                title="Refresh data"
              >
                {refreshing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                <span>Refresh</span>
              </button>
              
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date_sold">Date Sold</option>
                <option value="sale_price">Sale Price</option>
                <option value="address">Address</option>
                <option value="suitability">Suitability Score</option>
                <option value="import_order">Import Order</option>
              </select>
              
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {fetchingDetails && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Fetching Zillow property details and agent contacts...</span>
                <span>{fetchProgress.current} of {fetchProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${fetchProgress.total > 0 ? (fetchProgress.current / fetchProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
              {campaign?.campaign_subtype === 'under_contract_sellers_agent' && (
                <div className="mt-2 text-xs text-purple-600">
                  ✨ Enhanced process: Fetching property details + AI-powered agent contact discovery
                </div>
              )}
            </div>
          )}

          {/* Agent Details Progress Bar */}
          {fetchingAgentDetails && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Fetching agent contact details...</span>
                <span>{agentDetailsProgress.current} of {agentDetailsProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${agentDetailsProgress.total > 0 ? (agentDetailsProgress.current / agentDetailsProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Artwork Generation Progress Bar */}
          {generatingArtwork && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Generating artwork...</span>
                <span>{artworkProgress.current} of {artworkProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${artworkProgress.total > 0 ? (artworkProgress.current / artworkProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Watercolor Generation Progress Bar */}
          {generatingWatercolors && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Generating watercolor artwork from Zillow images...</span>
                <span>{watercolorProgress.current} of {watercolorProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${watercolorProgress.total > 0 ? (watercolorProgress.current / watercolorProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
              {campaign?.campaign_subtype === 'under_contract_sellers_agent' && (
                <div className="text-xs text-gray-500 mt-1">
                  ✨ Analyzing image suitability and generating professional watercolor artwork
                </div>
              )}
            </div>
          )}

          {/* Sequential Image Generation Progress Bar */}
          {generatingImages && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>
                  Generating {imageGenerationProgress.currentStyle} images - Property {imageGenerationProgress.currentProperty}/{imageGenerationProgress.totalProperties}
                </span>
                <span>{imageGenerationProgress.successfulImages} of {imageGenerationProgress.totalImages} completed</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${imageGenerationProgress.totalImages > 0 ? (imageGenerationProgress.successfulImages / imageGenerationProgress.totalImages) * 100 : 0}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-600">
                <div className="flex justify-between items-center">
                  <span>🏠 Current: {imageGenerationProgress.propertyAddress || 'Loading...'}</span>
                  <span>🎨 Style: {imageGenerationProgress.currentStyle || 'Preparing...'}</span>
                </div>
              </div>
              <div className="text-xs text-blue-600 mt-1">
                ✨ Processing in batches of up to 3 properties • Redfin and Zillow properties batch processed simultaneously
              </div>
            </div>
          )}

          {/* Image Generation Progress Message */}
          {imageGenerationMessage && (
            <div className="mt-4">
              <div className="text-sm px-3 py-2 rounded-lg bg-blue-100 text-blue-700">
                {imageGenerationMessage}
              </div>
            </div>
          )}

          {/* QR Code Generation Progress Message */}
          {qrCodeMessage && (
            <div className="mt-4">
              <div className="text-sm px-3 py-2 rounded-lg bg-blue-100 text-blue-700">
                {qrCodeMessage}
              </div>
            </div>
          )}

          {/* QR Code Generation Progress Bar */}
          {generatingQRCodes && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Generating QR codes...</span>
                <span>{qrCodeProgress.current} of {qrCodeProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                  style={{
                    width: `${qrCodeProgress.total > 0 ? (qrCodeProgress.current / qrCodeProgress.total) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Email Sending Progress Bar */}
          {sendingEmails && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Sending anniversary emails...</span>
                <span>{emailProgress.current} of {emailProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${emailProgress.total > 0 ? (emailProgress.current / emailProgress.total) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Email Error Message */}
          {emailError && (
            <div className="mt-4">
              <div className="text-sm px-3 py-2 rounded-lg bg-red-100 text-red-700">
                {emailError}
              </div>
            </div>
          )}

          {/* Test Email Success Message */}
          {testEmailSuccess && (
            <div className="mt-4">
              <div className="text-sm px-3 py-2 rounded-lg bg-green-100 text-green-700">
                {testEmailSuccess}
              </div>
            </div>
          )}

          {/* Refresh Message */}
          {refreshMessage && (
            <div className="mt-4">
              <div className={`text-sm px-3 py-2 rounded-lg ${
                refreshMessage.includes('Failed') 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-green-100 text-green-700'
              }`}>
                {refreshMessage}
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mt-4">
              <div className="text-sm px-3 py-2 rounded-lg bg-green-100 text-green-700">
                {successMessage}
              </div>
            </div>
          )}
        </div>

        {/* Properties Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {finalFilteredProperties.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <Building className="mx-auto h-16 w-16" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">No properties found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters' 
                  : 'This campaign has no properties yet'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked, finalFilteredProperties)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sale Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {campaign?.campaign_subtype === 'under_contract_sellers_agent' ? "Seller's Agent" : "Buyer's Agent"}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Suitability
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Artwork
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Oil Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pencil Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processing
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      QR Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tracking Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Landing
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Configure
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Checkout
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stripe
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {finalFilteredProperties.map((property) => (
                    <tr key={property.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedProperties.has(property.id)}
                          onChange={(e) => handleSelectProperty(property.id, e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {(() => {
                            // Determine source platform from campaign or property data
                            const sourcePlatform = campaign?.source_platform;
                            
                            let imageUrl = '';
                            
                            // Prioritize images based on source platform
                            if (sourcePlatform === 'redfin') {
                              imageUrl = property.redfin_main_image_url || property.homes_main_image_url || '';
                            } else if (sourcePlatform === 'zillow' || campaign?.campaign_subtype === 'under_contract_sellers_agent') {
                              imageUrl = property.zillow_image_url || property.homes_main_image_url || '';
                            } else {
                              // Default to homes.com for homes.com campaigns
                              imageUrl = property.homes_main_image_url || '';
                            }
                            
                            return imageUrl ? (
                              <img
                                src={imageUrl}
                              alt="Property"
                              className="w-16 h-16 rounded-lg object-cover mr-4"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                              <Building className="w-8 h-8 text-gray-400" />
                            </div>
                            );
                          })()}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {property.address_line_1 || 'No street address'}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {property.city}, {property.state} {property.zip_code}
                            </div>
                            {/* Debug info - show what would be used for artwork */}
                            <div className="text-xs text-blue-600 mt-1">
                              Artwork Address: {(() => {
                                const hasAddress = property.address_line_1 && typeof property.address_line_1 === 'string' && property.address_line_1.trim() !== '';
                                const hasCity = property.city && typeof property.city === 'string' && property.city.trim() !== '';
                                const hasState = property.state && typeof property.state === 'string' && property.state.trim() !== '';
                                const hasZip = property.zip_code && typeof property.zip_code === 'string' && property.zip_code.trim() !== '';
                                
                                if (hasAddress || hasCity || hasState) {
                                  const addressParts = [];
                                  if (hasAddress) {
                                    addressParts.push(property.address_line_1.trim());
                                  }
                                  const locationParts = [];
                                  if (hasCity) locationParts.push(property.city.trim());
                                  if (hasState) locationParts.push(property.state.trim());
                                  if (hasZip) locationParts.push(property.zip_code.trim());
                                  if (locationParts.length > 0) {
                                    addressParts.push(locationParts.join(', '));
                                  }
                                  return addressParts.join(', ');
                                } else {
                                  return `Property ${property.id}`;
                                }
                              })()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <DollarSign className="w-4 h-4 mr-1 text-green-600" />
                            {formatPrice(property.sale_price)}
                          </div>
                          <div className="flex items-center text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(property.date_sold)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            // For under contract campaigns, show seller agent info, otherwise show buyer agent info
                            const isUnderContractCampaign = campaign?.campaign_subtype === 'under_contract_sellers_agent';
                            
                            const agentName = isUnderContractCampaign 
                              ? property.sellers_agent_name 
                              : property.buyers_agent_name;
                            
                            const agentBrokerage = isUnderContractCampaign 
                              ? property.sellers_agent_brokerage 
                              : property.buyers_agent_brokerage;
                            
                            if (agentName) {
                              return (
                            <div>
                              <div className="flex items-center mb-1">
                                <User className="w-4 h-4 mr-1" />
                                    {agentName}
                                    {isUnderContractCampaign && (
                                      <span className="ml-2 px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">
                                        Seller's Agent
                                      </span>
                                    )}
                              </div>
                                  {agentBrokerage && (
                                <div className="flex items-center text-gray-500">
                                  <Building className="w-4 h-4 mr-1" />
                                      {agentBrokerage}
                                </div>
                              )}
                            </div>
                              );
                            } else {
                              return (
                                <span className="text-gray-400">
                                  No {isUnderContractCampaign ? 'seller' : 'buyer'} agent info
                                </span>
                              );
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            // For under contract campaigns, show seller agent contact info, otherwise show buyer agent contact info
                            const isUnderContractCampaign = campaign?.campaign_subtype === 'under_contract_sellers_agent';
                            
                            // Check for AI-found contact details first, then fall back to regular agent fields
                            const aiEmail = property.raw_data?.agentContactDetails?.aiFoundEmail;
                            const aiPhone = property.raw_data?.agentContactDetails?.aiFoundPhone;
                            const aiMobile = property.raw_data?.agentContactDetails?.aiFoundMobile;
                            
                            const email = aiEmail || (isUnderContractCampaign 
                              ? property.sellers_agent_email 
                              : property.buyers_agent_email);
                            
                            const phone = aiPhone || aiMobile || (isUnderContractCampaign 
                              ? property.sellers_agent_mobile 
                              : property.buyers_agent_mobile);
                            
                            const hasContactInfo = email || phone;
                            
                            if (hasContactInfo) {
                              return (
                                <div>
                                  {email && (
                                    <div className="flex items-center text-gray-500 mb-1">
                                      <Mail className="w-4 h-4 mr-1" />
                                      <span className="text-xs">{email}</span>
                                      {aiEmail && (
                                        <span className="ml-1 px-1 py-0.5 bg-green-100 text-green-800 text-xs rounded">AI</span>
                                      )}
                                    </div>
                                  )}
                                  {phone && (
                                    <div className="flex items-center text-gray-500">
                                      <Phone className="w-4 h-4 mr-1" />
                                      <span className="text-xs">{phone}</span>
                                      {(aiPhone || aiMobile) && (
                                        <span className="ml-1 px-1 py-0.5 bg-green-100 text-green-800 text-xs rounded">AI</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              return <span className="text-gray-400 text-xs">No contact details</span>;
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const { score, source, hasZillowImage } = getSuitabilityScoreDisplay(property);
                            if (score === undefined) {
                              return <span className="text-gray-400 text-xs">N/A</span>;
                            }
                            return (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSuitabilityScoreColor(score)}`}>
                                Score: {score}/10
                                {hasZillowImage && (
                                  <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Zillow</span>
                                )}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            // Check for artwork URL - prioritize dedicated columns, fallback to raw_data for existing data
                            const artworkUrl = property.watercolor_image_url ||
                                             property.oil_image_url ||
                                             property.pencil_image_url ||
                                             property.charcoal_image_url ||
                                             property.raw_data?.artworkGeneration?.artworkUrl; // Fallback for existing data
                            
                            if (artworkUrl) {
                              return (
                            <div className="flex items-center">
                              <img
                                    src={artworkUrl}
                                alt="Artwork"
                                className="w-10 h-10 rounded-md object-cover mr-2"
                              />
                              <div className="flex flex-col">
                                <span className="text-xs text-green-600">Generated</span>
                                    <span className="text-xs text-gray-500">
                                      {property.raw_data?.artworkGeneration?.style || 'watercolor'}
                                    </span>
                              </div>
                            </div>
                              );
                            } else if (property.raw_data?.artworkGeneration?.suitabilityScore !== undefined) {
                              return (
                            <div className="flex flex-col">
                              <span className="text-xs text-red-600">Skipped</span>
                              <span className="text-xs text-gray-500">Below threshold</span>
                              {property.raw_data.artworkGeneration.zillowImageUrl && (
                                <a 
                                  href={property.raw_data.artworkGeneration.zillowImageUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  View Source
                                </a>
                              )}
                                </div>
                              );
                            } else {
                              return <span className="text-gray-400 text-xs">Not Processed</span>;
                            }
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.oil_image_url ? (
                            <div className="flex items-center">
                              <img
                                src={property.oil_image_url}
                                alt="Oil Image"
                                className="w-10 h-10 rounded-md object-cover mr-2"
                              />
                              <div className="flex flex-col">
                                <span className="text-xs text-green-600">Generated</span>
                                <span className="text-xs text-gray-500">oil</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Not Generated</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.pencil_image_url ? (
                            <div className="flex items-center">
                              <img
                                src={property.pencil_image_url}
                                alt="Pencil Image"
                                className="w-10 h-10 rounded-md object-cover mr-2"
                              />
                              <div className="flex flex-col">
                                <span className="text-xs text-green-600">Generated</span>
                                <span className="text-xs text-gray-500">pencil</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Not Generated</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.processing_status}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.access_code ? (
                            <div className="flex items-center">
                              <span className="font-mono text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                {property.access_code}
                        </span>
                              <button
                                onClick={() => navigator.clipboard.writeText(propertyTokenGenerator.generateAccessCodeUrl(property.access_code))}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                                title="Copy configurator link"
                              >
                                📋
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No code</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.qr_code_url ? (
                            <div className="flex items-center">
                              <img 
                                src={property.qr_code_url} 
                                alt="QR Code"
                                className="w-12 h-12 border border-gray-200 rounded"
                              />
                              <button
                                onClick={() => window.open(property.qr_code_url, '_blank')}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                                title="View full size QR code"
                              >
                                🔍
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">No QR code</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const funnelStatus = getTrackingFunnelStatus(property);
                            return (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${funnelStatus.color}`}>
                                {funnelStatus.label}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      {/* Funnel Tracking Counter Cells - moved to correct position */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-blue-600">
                          {property.landing_page_accessed_count || 0}
                        </div>
                        {property.landing_page_accessed_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.landing_page_accessed_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-orange-600">
                          {property.configurator_opened_count || 0}
                        </div>
                        {property.configurator_opened_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.configurator_opened_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-yellow-600">
                          {property.order_summary_accessed_count || 0}
                        </div>
                        {property.order_summary_accessed_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.order_summary_accessed_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-purple-600">
                          {property.checkout_initiated_count || 0}
                        </div>
                        {property.checkout_initiated_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.checkout_initiated_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-indigo-600">
                          {property.payment_page_accessed_count || 0}
                        </div>
                        {property.payment_page_accessed_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.payment_page_accessed_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-green-600">
                          {property.purchase_completed_at ? '✓' : '0'}
                        </div>
                        {property.purchase_completed_at && (
                          <div className="text-xs text-gray-400">
                            {new Date(property.purchase_completed_at).toLocaleDateString()}
                          </div>
                        )}
                        {property.purchase_amount && (
                          <div className="text-xs font-medium text-green-700">
                            ${(property.purchase_amount / 100).toFixed(0)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          {property.homes_property_url && (
                            <a
                              href={property.homes_property_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 flex items-center"
                              title="View on Homes.com"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Homes
                            </a>
                          )}
                          {property.zillow_url && (
                            <a
                              href={property.zillow_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 hover:text-purple-800 flex items-center"
                              title="View on Zillow"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Zillow
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-7 gap-4">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-900">{finalFilteredProperties.length}</div>
            <div className="text-sm text-gray-500">
              {itemsPerPage === 'all' ? 
                (activeTab === 'with_emails' ? 'Properties with Agent Emails' : 
                 activeTab === 'ready_to_send' ? 'Ready to Send Properties' :
                 'Total Properties') :
                `Page ${currentPage} of ${totalPages} (${itemsPerPage} per page)`}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'ready_to_send').length}
            </div>
            <div className="text-sm text-gray-500">Ready to Send</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start mb-2">
              <div className="text-2xl font-bold text-blue-600">
                {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'email_sent').length}
            </div>
              {finalFilteredProperties.filter(p => 
                p.outreach_status === 'sent' || p.outreach_status === 'delivered' || 
                p.outreach_status === 'opened' || p.outreach_status === 'clicked'
              ).length > 0 && (
                <button
                  onClick={() => setShowResetEmailConfirm(true)}
                  disabled={resettingEmails}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  title="Reset email sent status for all properties"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500">Email Sent</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start mb-2">
              <div className="text-2xl font-bold text-orange-600">
                {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'property_opened').length}
            </div>
              {finalFilteredProperties.filter(p => 
                p.configurator_opened_count && p.configurator_opened_count > 0
              ).length > 0 && (
                <button
                  onClick={() => setShowResetOpenedConfirm(true)}
                  disabled={resettingOpened}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded transition-colors disabled:opacity-50"
                  title="Reset property opened status for all properties"
                >
                  Reset
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500">Property Opened</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-purple-600">
              {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'checkout_accessed').length}
            </div>
            <div className="text-sm text-gray-500">Checkout Accessed</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-indigo-600">
              {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'stripe_accessed').length}
        </div>
            <div className="text-sm text-gray-500">Stripe Accessed</div>
      </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">
              {finalFilteredProperties.filter(p => getTrackingFunnelStatus(p).status === 'purchased').length}
    </div>
            <div className="text-sm text-gray-500">Purchased</div>
          </div>
        </div>
      </div>
    </div>

    {/* Reset Email Confirmation Dialog */}
    {showResetEmailConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Reset Email Status</h3>
          </div>
          
          <p className="text-gray-600 mb-6">
            Are you sure you want to reset the email status for all properties? This will:
            <br />• Clear outreach status (sent, delivered, opened, clicked)
            <br />• Clear outreach sent timestamps
            <br />• Allow emails to be sent again to these properties
            <br /><br />
            <strong>{finalFilteredProperties.filter(p => 
              p.outreach_status === 'sent' || p.outreach_status === 'delivered' || 
              p.outreach_status === 'opened' || p.outreach_status === 'clicked'
            ).length} properties</strong> will be affected.
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowResetEmailConfirm(false)}
              disabled={resettingEmails}
              className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={resetEmailSentStatus}
              disabled={resettingEmails}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {resettingEmails ? (
                <>
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Resetting...
                </>
              ) : (
                'Reset Email Status'
              )}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Reset Property Opened Confirmation Dialog */}
    {showResetOpenedConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Reset Property Opened Status</h3>
          </div>
          
          <p className="text-gray-600 mb-6">
            Are you sure you want to reset the property opened status for all properties? This will:
            <br />• Reset configurator opened counts to 0
            <br />• Clear configurator opened timestamps
            <br />• Properties will appear as "not opened" in tracking
            <br /><br />
            <strong>{finalFilteredProperties.filter(p => 
              p.configurator_opened_count && p.configurator_opened_count > 0
            ).length} properties</strong> will be affected.
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowResetOpenedConfirm(false)}
              disabled={resettingOpened}
              className="flex-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={resetPropertyOpenedStatus}
              disabled={resettingOpened}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center"
            >
              {resettingOpened ? (
                <>
                  <svg className="w-4 h-4 animate-spin mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Resetting...
                </>
              ) : (
                'Reset Opened Status'
              )}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default CampaignPropertiesTable; 